import { SiloArtCanvasFor } from "@/components/SiloArtLayer";
import { ScaledGroup } from "@/components/ScaledGroup";
import { normalizeZones } from "@/lib/siloArt";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Warning, BookOpenText, Sparkle, Export, House, CreditCard, Lock, SpeakerHigh, SpeakerSimpleHigh, Pause, Play, Trash, Eye, Waveform, X, CaretLeft, CaretRight, CaretUp, CaretDown, List as HamburgerIcon, SlidersHorizontal } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { api, getUser, getToken } from "@/lib/api";
import { useViewerSurface } from "@/lib/useViewerSurface";
import { getRefSig, setRefSig, getFromSilo, setFromSilo, getVisitorSig, pushVisitedSilo } from "@/lib/attribution";
import ExportDialog from "@/components/ExportDialog";
import { CurvedListenButton } from "@/components/CurvedListenButton";
import { ConversationScrubber, buildScrubStops, stopIndexFor } from "@/components/ConversationScrubber";
import SideBumps from "@/components/SideBumps";
import DiscoverBumps from "@/components/DiscoverBumps";
import ShareConversationSheet from "@/components/ShareConversationSheet";
import DynamicBumps from "@/components/DynamicBumps";
import LengthBump from "@/components/LengthBump";
import SessionLlmPickerModal from "@/components/SessionLlmPickerModal";
import { readSessionLlmOverride, labelForModel, overrideAsAskBody } from "@/lib/sessionLlm";
import TipModal from "@/components/TipModal";
import SiloRestingBand from "@/components/SiloRestingBand";
import FundVoiceModal from "@/components/FundVoiceModal";
import SubscribePopup from "@/components/SubscribePopup";
import VoiceLaneCTA from "@/components/VoiceLaneCTA";
import A2HSPopup from "@/components/A2HSPopup";
import ReportContactModal from "@/components/ReportContactModal";
import SiloActionsDrawer from "@/components/SiloActionsDrawer"; // eslint-disable-line no-unused-vars -- kept for future; pill removed from Public Q&A Jul 2026
import OwnerByokNudgeModal from "@/components/OwnerByokNudgeModal";
import IdentityPill from "@/components/IdentityPill";
import SiloHeaderNav from "@/components/SiloHeaderNav";
import { useTabsOff } from "@/lib/tabToggle";
import BattleInboxBadge from "@/features/battles/BattleInboxBadge";
import StarSiloButton from "@/components/StarSiloButton";
import { useInteractions } from "@/lib/useInteractions";
import { Q4NudgePopup, FavoriteSignupCTA, CeilingExceededWall, HomeScreenHowTo } from "@/components/InteractionCTAs";
// Feb 19, 2026 iter-1 — owner-saveable composer resize. Isolated to one
// component so it can be cleanly rolled back if it misbehaves.
import ComposerResizable from "@/components/ComposerResizable";
import { useVoiceQA, VOICE_OPTIONS, chunkForSpeech } from "@/lib/useVoiceQA";
import VoucherTopupModal from "@/components/VoucherTopupModal";
// Feb 19, 2026 iter-2 — draggable divider between chat history + composer.
// Session-only; squeeze cascade hides footer/pill/tip when area shrinks.
import ComposerAreaSplitter from "@/components/ComposerAreaSplitter";
// BattleHighlights removed from PublicSilo Feb 14, 2026 — keeping the
// import out as well; component file still lives at
// /features/battles/BattleHighlights.jsx if we ever resurface it.
import { useBumpConfig } from "@/lib/bumpConfig";
import useSiloManifest from "@/lib/useSiloManifest";

// Auto-detect bare URLs in plain text and convert them to Markdown links so
// ReactMarkdown will render them clickable. Skips URLs that are already inside
// a Markdown link (e.g. [label](https://…)) to avoid double-wrapping. We also
// skip image markdown and code-fenced blocks (we want raw URLs in code/fenced
// content to stay literal).
function linkifyUrls(text) {
  if (!text) return text;
  // Strip out fenced code blocks before detection, then re-inject so URLs in
  // code samples don't get linkified.
  const codeBlocks = [];
  let i = 0;
  const stashed = text.replace(/```[\s\S]*?```/g, (m) => {
    codeBlocks.push(m);
    return `\u0000CODEBLOCK_${i++}\u0000`;
  });
  // Stash existing markdown links so we don't double-wrap them.
  const mdLinks = [];
  let j = 0;
  const stashed2 = stashed.replace(/!?\[[^\]]*\]\([^)]*\)/g, (m) => {
    mdLinks.push(m);
    return `\u0000MDLINK_${j++}\u0000`;
  });
  // Match http(s)://… and bare www.* — stop at whitespace, common punctuation
  // boundaries, or markdown chars. Keep trailing dot/comma/paren OUT of the URL.
  const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>()[\]'"`*]+[^\s<>()[\]'"`*.,!?;:])/g;
  const linkified = stashed2.replace(URL_RE, (raw) => {
    const href = raw.startsWith("www.") ? `https://${raw}` : raw;
    return `[${raw}](${href})`;
  });
  // Re-inject stashed segments.
  let out = linkified;
  out = out.replace(/\u0000MDLINK_(\d+)\u0000/g, (_m, idx) => mdLinks[Number(idx)]);
  out = out.replace(/\u0000CODEBLOCK_(\d+)\u0000/g, (_m, idx) => codeBlocks[Number(idx)]);
  return out;
}

export default function PublicSilo() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const viewer = useViewerSurface();

  // Feb 2026 (Randy pt-md-qa-scroll) — when this page is rendered inside
  // the Master Dashboard's Q&A iframe (URL param `?embedded=1`), disable
  // the browser's default scroll-into-view-on-focus behavior for inputs.
  // The MD iframe uses transform: scale(0.55), which confuses the browser's
  // coordinate math and causes the whole conversation to jump upward when
  // the "Ask ___" input gets focus. Fix: block scrollIntoView calls that
  // originate from focus, and constrain scroll to the input's own container.
  useEffect(() => {
    const isEmbedded = new URLSearchParams(window.location.search).get("embedded") === "1";
    if (!isEmbedded) return;
    const style = document.createElement("style");
    style.textContent = `
      html, body { scroll-behavior: auto !important; overflow-anchor: none !important; }
      input:focus, textarea:focus { scroll-margin: 0 !important; }
    `;
    document.head.appendChild(style);
    // Guard scrollIntoView calls that fire from focus handlers. We keep the
    // original on the prototype so any explicit scroll (e.g. "scroll to
    // latest message" logic in this page) still works — we just filter
    // out the ones that originate from focus events.
    const orig = HTMLElement.prototype.scrollIntoView;
    let inFocus = false;
    const onFocusIn = () => { inFocus = true; setTimeout(() => { inFocus = false; }, 60); };
    document.addEventListener("focusin", onFocusIn, true);
    HTMLElement.prototype.scrollIntoView = function (...args) {
      if (inFocus) return;
      return orig.apply(this, args);
    };
    return () => {
      HTMLElement.prototype.scrollIntoView = orig;
      document.removeEventListener("focusin", onFocusIn, true);
      style.remove();
    };
  }, []);

  const [silo, setSilo] = useState(null);
  const [stats, setStats] = useState({ document_count: 0, total_characters: 0 });
  // Feb 17, 2026 (Randy pt-public-qa-live-mode). The public silo endpoint
  // computes `silo_art_v2` server-side from the creator's dashboard mode
  // (Off/V1/V2). When the creator flips the trio toggle in the identity
  // pill WHILE viewing their own public Q&A, the SiloArtProvider fires a
  // `mysilo:silo-art-mode-persisted` event AFTER the /me/silo-art PATCH
  // resolves (so the backend has durably applied the change). We
  // refetch the silo here so `silo.silo_art_v2` reflects the new value
  // instantly — no page refresh needed. Anonymous fans never trigger
  // this event so it's a no-op for them.
  useEffect(() => {
    if (!slug) return;
    const onPersisted = async () => {
      try {
        const _bmq = (() => { try { return new URLSearchParams(window.location.search).get("bookmark") || ""; } catch { return ""; } })();
        const _qs = _bmq ? `?bookmark=${encodeURIComponent(_bmq)}` : "";
        const r = await api.get(`/s/${slug}${_qs}`);
        setSilo(r.data);
      } catch { /* non-fatal */ }
    };
    window.addEventListener("mysilo:silo-art-mode-persisted", onPersisted);
    return () => window.removeEventListener("mysilo:silo-art-mode-persisted", onPersisted);
  }, [slug]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // May 24, 2026 — distinct state for "silo exists but you need to sign
  // in to see it" (typically a hidden silo where the handoff token from
  // the home-screen icon expired). Different UX from the cold "no such
  // silo" page — we send the user to /login with a return URL.
  const [needsLogin, setNeedsLogin] = useState(false);
  const [nudgeTick, setNudgeTick] = useState(0); // iter-35a-fix: re-render after dismiss
  // Feb 23, 2026 — Option B: visitor chat is now browser-session-scoped
  // only. We do NOT persist the session_id to localStorage between visits,
  // and we do NOT call the server's rehydration endpoint on mount. Each
  // fresh page load gets a fresh session (no "remembered last question").
  //
  // The ?session=<id> URL param still works — it's how the creator opens
  // a *specific* prior conversation from their own dashboard. That's an
  // explicit signal of intent; we honor it.
  const [sessionId, setSessionId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("session");
      if (fromUrl) return fromUrl;
      return "";
    } catch { return ""; }
  });
  // Jun 2026 (Randy) — a bookmark loaded into the public Q&A (its uuid lives in
  // the URL as ?bookmark=<id>) so refresh/share keep the EXACT thread.
  const [loadedBookmarkId, setLoadedBookmarkId] = useState("");
  const [shareConvOpen, setShareConvOpen] = useState(false);
  // Jun 2026 (Randy) — the loaded bookmark's audio status. When a shared/loaded
  // conversation arrives WITH a complete + still-alive sound file, the premium
  // "Listen" button flips from forest-green (with cost) to BRIGHT-GREEN (pre-paid)
  // and plays free for ANY visitor (anon/fan/creator/affiliate) — it came loaded.
  const [loadedBookmarkAudio, setLoadedBookmarkAudio] = useState({ complete: false, live: false });
  // Jun 2026 (Randy) — OPEN-TIME "complete the audio?" popup for the bookmark
  // OWNER loading their own partial-audio bookmark on the public Q&A. Accept =
  // record the missing parts (their own key) so it becomes complete (→ free play
  // for everyone they share it with); decline = keep partial.
  const [completeAudioPopup, setCompleteAudioPopup] = useState(null); // { bmId, segs, missCount, cents }
  const [completingAudio, setCompletingAudio] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  // iter-26 anonymous quota state REMOVED Jun 2026 (clean-slate teardown).
  // Sprint Feb 8, 2026 — owner BYOK nudge modal (fires when /ask returns
  // 402 OWNER_BYOK_NOT_SET). Visitor-friendly path that lets them email
  // the silo owner instead of seeing a brutal "go add a Gemini key" error.
  const [ownerByokNudge, setOwnerByokNudge] = useState({ open: false, ownerName: "" });
  const [tipOpen, setTipOpen] = useState(false);
  // Feb 18, 2026 — unified subscribe popup (free + paid)
  const [subscribePopupOpen, setSubscribePopupOpen] = useState(false);
  const [becomeCreatorCtaOpen, setBecomeCreatorCtaOpen] = useState(false);
  const [outOfDotsCtaOpen, setOutOfDotsCtaOpen] = useState(false);
  const [voiceSetupCtaOpen, setVoiceSetupCtaOpen] = useState(false);
  // Jun 2026 (Randy) — shown to a VISITOR who tries to voice a new piece but the
  // creator never funded premium voice (silo.voice_ready === false).
  const [creatorVoiceOffOpen, setCreatorVoiceOffOpen] = useState(false);
  const [fundVoiceOpen, setFundVoiceOpen] = useState(false);
  // Jun 2026 (Randy) — mobile-only hamburger that bundles Home / Clear / Export
  // in the top-left corner so those buttons stop overlapping the silo logo.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Tiny eye-tooltip next to "Speak" with iOS instructions for a nicer free voice.
  const [voiceTipOpen, setVoiceTipOpen] = useState(false);
  const [leftTipOpen, setLeftTipOpen] = useState(false);
  const [rightTipOpen, setRightTipOpen] = useState(false);
  // Jun 2026 (Randy) — smart whole-conversation playback: cache coverage (so the
  // cost shows only the MISSING parts and the pill goes green when fully cached),
  // scrubber position, and the "play from beginning vs latest" choice popup.
  const [coverage, setCoverage] = useState({ cached: [], missingChars: 0, allCached: false });
  // Jun 2026 (Randy) — bumped after we re-mint the voice cache from a shared
  // bookmark's pre-paid audio, to force the coverage probe to re-run and light
  // up the now-cached turns (green icons + free scrubber replay + dots-on-new).
  const [hydrateNonce, setHydrateNonce] = useState(0);
  const [playPos, setPlayPos] = useState({ msgIdx: -1, frac: 0 });
  const [playChoiceOpen, setPlayChoiceOpen] = useState(false);
  const pendingPlayModeRef = useRef("self");
  const lastModeRef = useRef("self");
  // The three listen badges collapse into / expand out of the "Listen to whole
  // conversation" phrase when it's tapped (default collapsed).
  const [lanesOpen, setLanesOpen] = useState(false);
  // Jul 2026 (Randy) — listen pill is now a CAROUSEL: show one option at a time
  // (Free · You-pay · Silo-pays) with left/right arrows + swipe. Default lands on
  // the paid lane (index 1). laneTouchX tracks a horizontal swipe on touch.
  const [laneIndex, setLaneIndex] = useState(1);
  const laneTouchX = useRef(null);
  // Shared Home navigation (used by both the desktop Home button and the mobile
  // hamburger). Goes to this creator's marketing page while preserving any
  // existing referral seed (don't overwrite another inviter's attribution).
  const goHome = () => {
    const existing = (typeof window !== "undefined")
      ? (window.localStorage.getItem("mysilo_ref_sig") || "")
      : "";
    const ownerCode = silo?.owner_referral_code || "";
    const refCode = existing && existing !== ownerCode ? "_" : (ownerCode || "_");
    navigate(`/m/${refCode}/${encodeURIComponent(silo.slug)}`);
  };
  // Jun 2026 — Visitor billing ("dots") state + CTA orchestration.
  const interactions = useInteractions(slug, { enabled: !!silo });
  const [q4Open, setQ4Open] = useState(false);
  const [favCtaOpen, setFavCtaOpen] = useState(false);
  // Jun 2026 (Randy) — count the anon's OWN questions this session (NOT preloaded
  // shared-bookmark turns) to time the "keep this conversation" sign-up nudge.
  const [anonAskCount, setAnonAskCount] = useState(0);
  // Anon clicked the "keep this conversation" nudge → after they sign up in-place
  // we claim their session so it's recallable on any device (no tools drawer).
  const pendingKeepConvoRef = useRef(false);
  // When the in-place sign-up card is opened from the STAR it also favorites;
  // from the header "Sign up" button it's a pure sign-up (no auto-star).
  const [favCtaStar, setFavCtaStar] = useState(true);
  const [ceilingWallOpen, setCeilingWallOpen] = useState(false);
  const [homeHowToOpen, setHomeHowToOpen] = useState(false);
  const seenUnlockIntroRef = useRef(false);
  const pendingTokenQRef = useRef(null);
  // Anon clicked "Subscribe" → after they sign up in-place, chain into the
  // Subscribe popup (site sign-up and silo subscription are two clean steps).
  const pendingSubscribeRef = useRef(false);
  const [reportOpen, setReportOpen] = useState(false);
  // Iter160e (Randy) — session-only LLM surface picker for Public Q&A.
  // Pick is remembered in sessionStorage until the tab is closed or the
  // user hits refresh. Both the visitor and the owner can use it.
  const [llmPickerOpen, setLlmPickerOpen] = useState(false);
  const [llmOverrideTick, setLlmOverrideTick] = useState(0);
  const llmOverride = useMemo(() => readSessionLlmOverride("public_ask"), [llmOverrideTick]);
  const feedRef = useRef(null);
  // Jul 2026 (Randy) — measure the sticky header height so the "Listen to whole
  // conversation" shelf can freeze itself as its own locked bar directly BELOW
  // the header (mirrors the AI Assistant), with the Q&A scrolling behind it.
  const [headerH, setHeaderH] = useState(72);
  const headerRoRef = useRef(null);
  const headerRef = useCallback((node) => {
    if (headerRoRef.current) { headerRoRef.current.disconnect(); headerRoRef.current = null; }
    if (node) {
      setHeaderH(node.offsetHeight || 72);
      if (typeof ResizeObserver !== "undefined") {
        headerRoRef.current = new ResizeObserver(() => setHeaderH(node.offsetHeight || 72));
        headerRoRef.current.observe(node);
      }
    }
  }, []);
  const messageRefs = useRef([]); // per-message DOM nodes, for follow-along scroll
  // Feb 22, 2026 — Staging push deep-link auto-fire. Declared up here
  // (above the loading/notFound early returns) so Rules of Hooks is
  // honored. `askRef` is filled in once `ask` is defined further down.
  const autoFiredRef = useRef(false);
  const askRef = useRef(null);
  // Jun 2026 — Voice conversation (Public Q&A). Declared above the loading /
  // notFound early returns so Rules of Hooks holds. The hook drives the
  // EXISTING grounded answer flow through askRef, and reads the visitor's
  // chosen voice from selectedVoiceRef (only used when the creator allows it).
  // Jun 2026 (Randy) — the visitor's voice pick is SESSION-ONLY: it's remembered
  // while they're here, but a refresh / fresh visit falls back to the creator's
  // per-silo setting (silo.voice_id, default Onyx). So we start with no override.
  const [selectedVoice, setSelectedVoice] = useState(null);
  const selectedVoiceRef = useRef(null);
  selectedVoiceRef.current = selectedVoice;
  // Jun 2026 — Spoken-answer voice is a PROFILE preference, not a per-silo
  // setting: when a user picks a voice it follows them across every silo. Save
  // to localStorage (instant, works for anon) + their profile (if signed in).
  const onSelectVoice = useCallback((v) => {
    // Session-only — not persisted; a refresh reverts to the creator's setting.
    setSelectedVoice(v);
  }, []);
  // Jun 2026 — Voice money-guard (OpenAI lane). When the owner hasn't funded
  // voice, the backend raises 402 LLM_GUARD (provider=openai). For a regular
  // visitor we show a gentle "not turned on yet" bubble; for the OWNER viewing
  // their own page we open the OpenAI top-up CTA so they can fund it.
  const [voiceTopupOpen, setVoiceTopupOpen] = useState(false);
  const [textTopupOpen, setTextTopupOpen] = useState(false);
  const siloRef = useRef(null);
  siloRef.current = silo;
  const onVoiceGuardBlocked = useCallback(() => {
    const s = siloRef.current;
    if (s?.viewer_is_owner) {
      setVoiceTopupOpen(true);
      return;
    }
    setMessages((m) => [...m, {
      role: "ai", error: true,
      text: `**${(s && s.name) || "This silo"} hasn't turned on voice yet.** The creator needs to connect their voice engine — check back soon.`,
    }]);
  }, []);
  const voiceAsk = useCallback(
    (text) => (askRef.current ? askRef.current(text) : Promise.resolve(null)),
    [],
  );
  const getVoiceId = useCallback(() => selectedVoiceRef.current || undefined, []);
  // Jun 2026 (Randy) — the QUESTION voice for two-voice whole-conversation
  // playback (one slot after the answer voice), computed fresh so the live voice
  // chat can warm the spoken question into the cache in the SAME voice the Listen
  // pill probes. Mirrors the `questionVoice` memo below exactly.
  const getQuestionVoiceId = useCallback(() => {
    const av = selectedVoiceRef.current || silo?.voice_id || "onyx";
    const aIdx = VOICE_OPTIONS.findIndex((o) => o.id === av);
    return VOICE_OPTIONS[((aIdx < 0 ? 0 : aIdx) + 1) % VOICE_OPTIONS.length].id;
  }, [silo?.voice_id]);
  // Jun 2026 — Follow-along scroll controller. The hook reports, for each
  // spoken chunk, which message is being read and the start/end fraction of
  // THAT message's text the chunk covers, plus the chunk's audio duration. We
  // animate the window smoothly from the start-fraction point to the end point
  // over that duration, keeping the line being read ~40% down the screen. This
  // replaces the old "center the whole bubble once" jump (which stranded long
  // answers off-screen). Works for every path: the 3 Listen lanes, the per-
  // answer replay, AND live speech-to-speech ("LIVE" → the latest answer).
  const lastAiIdxRef = useRef(-1);
  const scrollAnimRef = useRef(null);
  // Jun 2026 (Randy) — manual-scroll override. While a reply is read aloud the
  // page follows the words; the moment the visitor scrolls/swipes by hand we
  // stop hijacking their scroll for 5s, then gently resume following.
  const userScrolledRef = useRef(false);
  const userScrollTimerRef = useRef(null);
  const onScrollTick = useCallback((messageIndex, fromFrac, toFrac, durationMs) => {
    const idx = messageIndex === "LIVE" ? lastAiIdxRef.current : messageIndex;
    // Drive the conversation scrubber (which bubble + how far through it).
    if (typeof idx === "number") setPlayPos({ msgIdx: idx, frac: toFrac });
    const el = messageRefs.current[idx];
    if (!el || typeof window === "undefined") return;
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    const rect0 = el.getBoundingClientRect();
    const elDocTop = window.scrollY + rect0.top;     // stable during playback
    const elH = el.offsetHeight || rect0.height || 1;
    const anchor = window.innerHeight * 0.40;         // keep reading line ~40% down
    const fromY = Math.max(0, elDocTop + fromFrac * elH - anchor);
    const toY = Math.max(0, elDocTop + toFrac * elH - anchor);
    const dur = Math.max(300, durationMs || 800);
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; // easeInOutQuad
      if (!userScrolledRef.current) window.scrollTo(0, fromY + (toY - fromY) * e);
      if (p < 1) scrollAnimRef.current = requestAnimationFrame(step);
    };
    scrollAnimRef.current = requestAnimationFrame(step);
  }, []);
  // Jul 2026 (Randy) — after posting a NEW question, glide it up so it sits at
  // the TOP of the screen. The thinking dots are then visible right beneath it
  // and the answer streams in below — no more scrolling/swiping to hunt for
  // where the question landed.
  const scrollQuestionToTop = useCallback((idx) => {
    const run = () => {
      const el = messageRefs.current[idx];
      if (!el || typeof window === "undefined") return;
      const header = document.querySelector("header");
      const shelf = document.querySelector('[data-testid="listen-shelf-sticky"]');
      const offset = (header?.offsetHeight || 72) + (shelf?.offsetHeight || 0) + 10;
      const targetY = Math.max(0, window.scrollY + el.getBoundingClientRect().top - offset);
      userScrolledRef.current = false;
      if (scrollAnimRef.current) { cancelAnimationFrame(scrollAnimRef.current); scrollAnimRef.current = null; }
      try { window.scrollTo({ top: targetY, behavior: "smooth" }); } catch { window.scrollTo(0, targetY); }
    };
    requestAnimationFrame(() => setTimeout(run, 50));
  }, []);
  // Detect manual scrolling so audio playback can't lock the visitor's screen.
  // Jul 2026 (Randy) — mirror the creator's loose-text readability prefs on the
  // fan-facing page (bold / all-black text). The provider that normally sets
  // these body classes only runs for logged-in creators, so drive them here
  // from the public silo payload.
  useEffect(() => {
    const bold = !!(silo && silo.silo_art_bold);
    const boldMobile = !!(silo && silo.silo_art_bold_mobile);
    const black = !!(silo && silo.silo_art_black);
    const blackMobile = !!(silo && silo.silo_art_black_mobile);
    document.body.classList.toggle("siloart-bold", bold);
    document.body.classList.toggle("siloart-bold-mobile", boldMobile);
    document.body.classList.toggle("siloart-blacktext", black);
    document.body.classList.toggle("siloart-blacktext-mobile", blackMobile);
    return () => {
      document.body.classList.remove("siloart-bold");
      document.body.classList.remove("siloart-bold-mobile");
      document.body.classList.remove("siloart-blacktext");
      document.body.classList.remove("siloart-blacktext-mobile");
    };
  }, [silo && silo.silo_art_bold, silo && silo.silo_art_bold_mobile, silo && silo.silo_art_black, silo && silo.silo_art_black_mobile]);


  useEffect(() => {
    const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Spacebar"]);
    const onManual = () => {
      userScrolledRef.current = true;
      if (userScrollTimerRef.current) clearTimeout(userScrollTimerRef.current);
      userScrollTimerRef.current = setTimeout(() => { userScrolledRef.current = false; }, 5000);
    };
    const onKey = (e) => { if (SCROLL_KEYS.has(e.key)) onManual(); };
    window.addEventListener("wheel", onManual, { passive: true });
    window.addEventListener("touchmove", onManual, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", onManual);
      window.removeEventListener("touchmove", onManual);
      window.removeEventListener("keydown", onKey);
      if (userScrollTimerRef.current) clearTimeout(userScrollTimerRef.current);
    };
  }, []);
  const voice = useVoiceQA({ slug, ask: voiceAsk, getVoiceId, getQuestionVoiceId, onGuardBlocked: onVoiceGuardBlocked, onSelfNoFunding: () => setVoiceSetupCtaOpen(true), voiceReady: !!silo?.voice_ready, onScrollTick });

  // --- Whole-conversation playback model (shared by lanes + scrubber + popup) ---
  const { answerVoice, questionVoice } = useMemo(() => {
    const av = selectedVoice || silo?.voice_id || "onyx";
    const aIdx = VOICE_OPTIONS.findIndex((o) => o.id === av);
    const qv = VOICE_OPTIONS[((aIdx < 0 ? 0 : aIdx) + 1) % VOICE_OPTIONS.length].id;
    return { answerVoice: av, questionVoice: qv };
  }, [selectedVoice, silo?.voice_id]);

  const allTurns = useMemo(
    () =>
      (messages || [])
        .map((m, i) => ({ role: m.role === "user" ? "user" : "assistant", text: (m.text || "").trim(), idx: i, err: m.error }))
        .filter((t) => t.text && !t.err),
    [messages],
  );

  // One scrubber dot per Q+A pair (a question + the answer beneath it).
  const qaSegments = useMemo(() => {
    const segs = [];
    let q = null;
    (messages || []).forEach((m, i) => {
      const txt = (m.text || "").trim();
      if (!txt || m.error) return;
      if (m.role === "user") q = { qMsgIdx: i, qText: txt };
      else { segs.push({ qMsgIdx: q ? q.qMsgIdx : i, aMsgIdx: i, label: (q ? q.qText : txt).slice(0, 48) }); q = null; }
    });
    return segs;
  }, [messages]);

  // Flat scrubber stops: one dot where each question starts, one (larger) where
  // its answer starts. Nothing inside an answer. (Randy, Sep 2026 — pulled back
  // from the interval-dot version that buried the line in dots.)
  const scrubStops = useMemo(
    () =>
      buildScrubStops(
        qaSegments.map((s) => ({
          qMsgIdx: s.qMsgIdx,
          qText: messages[s.qMsgIdx]?.text || "",
          aMsgIdx: s.aMsgIdx,
          aText: messages[s.aMsgIdx]?.text || "",
        })),
      ),
    [qaSegments, messages],
  );

  // SCOPE: null = the whole conversation. While a single exchange is playing
  // (the per-bubble speaker), the drawer narrows to that pair's two dots so the
  // listener can skip a long question. It snaps back when that playback ends.
  const [scrubScope, setScrubScope] = useState(null);
  const visibleStops = useMemo(
    () => (scrubScope ? scrubStops.filter((s) => s.segIdx === scrubScope.segIdx) : scrubStops),
    [scrubScope, scrubStops],
  );
  useEffect(() => {
    if (!scrubScope) return;
    // Any other audio taking over (incl. the whole-conversation read) or
    // playback finishing → back to the full picture.
    if (voice.playKey !== scrubScope.key || voice.state === "idle") setScrubScope(null);
  }, [scrubScope, voice.playKey, voice.state]);

  // The ONE playback entry point for both pipelines: `scope` null reads the
  // conversation onward from that stop; a scope reads only that exchange.
  const playStop = useCallback(
    (stops, stopIdx, mode, scope) => {
      const st = stops[Math.max(0, stopIdx)];
      if (!st) return;
      const m = mode || lastModeRef.current || "self";
      lastModeRef.current = m;
      const endIdx = scope ? (qaSegments[st.segIdx]?.aMsgIdx ?? st.msgIdx) : Infinity;
      const sliced = allTurns.filter((t) => t.idx >= st.msgIdx && t.idx <= endIdx);
      if (!sliced.length) return;
      setScrubScope(scope || null);
      voice.speakConversation(sliced, { answerVoice, questionVoice, mode: m, key: scope ? scope.key : "scrub" });
    },
    [qaSegments, allTurns, answerVoice, questionVoice, voice],
  );

  // A STABLE signature of what we'd probe, so the cache check fires once per
  // real content/voice change — never on every render. (probeCached POSTs to
  // the voice endpoint; re-running it each render burns real TTS spend.)
  const probeSig = useMemo(
    () => allTurns.map((t) => `${t.idx}:${t.role}:${t.text.length}`).join("|") + `~${answerVoice}/${questionVoice}`,
    [allTurns, answerVoice, questionVoice],
  );
  const lastProbeSigRef = useRef("");
  const prevVoiceStateRef = useRef("idle");

  // Probe the 24h cache (cost-free) so the pill shows only the MISSING cost and
  // flips green when the whole thread is already paid. Fires once per content/
  // voice change while idle, plus once right after playback finishes (to pick up
  // newly-cached audio). Guarded so it can never loop or re-probe needlessly.
  useEffect(() => {
    if (!silo?.voice_enabled || !allTurns.length) {
      lastProbeSigRef.current = "";
      setCoverage((c) => (!c.cached.length && !c.missingChars && !c.allCached ? c : { cached: [], missingChars: 0, allCached: false }));
      return;
    }
    if (voice.state !== "idle") { prevVoiceStateRef.current = voice.state; return; }
    const justFinished = prevVoiceStateRef.current !== "idle";
    prevVoiceStateRef.current = "idle";
    if (probeSig === lastProbeSigRef.current && !justFinished) return;
    lastProbeSigRef.current = probeSig;
    let cancelled = false;
    const t = setTimeout(async () => {
      const cached = [];
      let missingChars = 0;
      for (const turn of allTurns) {
        const v = turn.role === "user" ? questionVoice : answerVoice;
        let hit = false;
        try { hit = await voice.probeCached(turn.text, v); } catch { hit = false; }
        if (cancelled) return;
        if (hit) cached.push(turn.idx);
        else missingChars += turn.text.replace(/\s+/g, " ").trim().length;
      }
      if (!cancelled) setCoverage({ cached, missingChars, allCached: cached.length === allTurns.length });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [probeSig, allTurns, answerVoice, questionVoice, silo?.voice_enabled, voice.state, voice.probeCached, hydrateNonce]);

  const scrubCurrentStop = stopIndexFor(visibleStops, playPos.msgIdx);
  // Does the audio playing right now belong to what the drawer is showing? The
  // transport must never claim to be playing a per-bubble pair as if it were
  // the whole-conversation read. (Randy, Sep 2026)
  const scrubOwnsAudio = scrubScope ? voice.playKey === scrubScope.key : voice.playKey === "scrub";
  // Track the most-recent answer bubble so live speech-to-speech can follow it.
  useEffect(() => {
    let idx = -1;
    for (let i = messages.length - 1; i >= 0; i--) { if (messages[i].role !== "user") { idx = i; break; } }
    lastAiIdxRef.current = idx;
  }, [messages]);
  // Stop any follow-scroll the moment playback ends.
  useEffect(() => {
    if (voice.state === "idle" && scrollAnimRef.current) {
      cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
    }
  }, [voice.state]);
  // Feb 14, 2026 — Visitor-side answer style. Resolved by the LengthBump
  // on mount (silo default or last-saved session style) and ridden along
  // with each /ask payload. Updated when the visitor picks from the bump.
  const [answerStyle, setAnswerStyle] = useState({ preset: null, custom: null });

  // iter-39 Sprint 2 — chat aesthetics from BumpStudio (tip badge style/color,
  // sources strip toggle). Falls back to platform defaults when cfg is loading.
  const { cfg: bumpCfg } = useBumpConfig();
  const ca = bumpCfg?.chat_aesthetics || {};
  const tipStyle = ca.tip_badge_style || "pill";
  const tipBg = ca.tip_badge_color || "#6B6A68";
  const tipFg = ca.tip_badge_text_color || "#FFFFFF";

  // Jul 25, 2026 (Randy) — "Turn off tabs" also relocates Subscribe + Tip into
  // the hamburger dropdown (tip second-to-bottom, subscribe last), leaving only
  // the composer at the bottom. Shared visibility + handlers used by BOTH the
  // under-composer pills (tabs on) and the dropdown footer (tabs off).
  const tabsOff = useTabsOff();
  const pillUser = (typeof getUser === "function" ? getUser() : null);
  // Sep 23, 2026 (Randy) — Subscribe and Tip used to share ONE visibility flag
  // (`showSubTip`), so the moment a fan subscribed the Tip button vanished
  // along with the Subscribe pill. Hiding Subscribe from someone already
  // subscribed is right; hiding Tip is not. A tip is not a route to access,
  // it is how a fan says thank you — and a paying subscriber is the likeliest
  // person to want to. Per Randy: the tip option is ALWAYS available, to
  // everyone, with no exceptions. Only Subscribe is conditional.
  //
  // The old flag also tested `viewer_tier === "owner"` / `"paying_subscriber"`,
  // but /api/s/{slug} never returns `viewer_tier` at all (see server.py:15342 —
  // it returns `viewer_is_owner` / `viewer_is_subscriber`). Those two checks
  // were dead, which is why an owner still saw "Subscribe to your own silo" on
  // their own page even though the fan checkout endpoint rejects it with a 409.
  const viewerIsOwner = !!pillUser
    && (!!silo?.viewer_is_owner || silo?.viewer_tier === "owner");
  const viewerIsSubscriber = !!pillUser
    && (!!silo?.viewer_is_subscriber || silo?.viewer_tier === "paying_subscriber");
  const showSubscribe = !!silo && !viewerIsOwner && !viewerIsSubscriber;
  const showTip = !!silo;
  // Sep 25, 2026 (Randy) — the pill quotes the SUPPORTER'S TOTAL (price +
  // service fee) so it agrees with the Subscribe popup and the Stripe page
  // before anyone taps. Falls back to the creator's price on a stale payload.
  const subDollars = ((silo?.subscriber_total_cents ?? silo?.subscriber_price_cents ?? 0)) / 100;
  const subPriceLabel = `$${subDollars.toFixed(subDollars % 1 === 0 ? 0 : 2)}/mo`;
  const onSubscribePillClick = () => {
    if (pillUser) { setSubscribePopupOpen(true); return; }
    pendingSubscribeRef.current = true;
    setFavCtaStar(false);
    setFavCtaOpen(true);
  };

  // Jun 2026 (Randy) — the ghost star. A signed-out visitor favorites, the dot
  // lands now, and signing in later converts it to a real favorite server-side.
  // Un-starring refunds the dot unless it has already bought a question.
  const ghostStarred = !!(interactions.state?.board || [])
    .find((t) => t.key === "favorite")?.earned;
  const toggleGhostStar = async (next) => {
    try {
      const r = await api.post(`/s/${slug}/interactions/ghost-star`, { starred: next });
      if (r.data?.interactions) interactions.applyFromAsk(r.data.interactions);
      if (r.data?.error === "dot_spent") {
        toast("You already used that dot on a question — the star stays with you.");
        return false;
      }
      if (next) {
        toast.success("Favorited — that's one more question.", {
          action: { label: "Sign up free", onClick: () => { setFavCtaStar(true); setFavCtaOpen(true); } },
          description: "Sign up free and we'll keep this favorite on your account.",
        });
      } else {
        toast("Removed — that question went back too.");
      }
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that right now.");
      return false;
    }
  };

  // iter-25: stable per-browser visitor signature cookie so the creator's
  // Recent Conversations tab can recognize returning fans across sessions.
  useEffect(() => {
    try {
      const existing = document.cookie.split("; ").find((c) => c.startsWith("mysilo_visitor_sig="));
      if (!existing) {
        const sig = (crypto.randomUUID?.() || Math.random().toString(36).slice(2)).replace(/-/g, "").slice(0, 10);
        // 400-day expiry — effectively persistent until user clears cookies
        const exp = new Date(Date.now() + 400 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `mysilo_visitor_sig=${sig}; path=/; expires=${exp}; SameSite=Lax`;
      }
    } catch { /* ignore */ }
  }, []);

  // iter-31 + P0 (Feb 7, 2026): when arriving with ?ref=<sig>, stash it for
  // signup AND record a pending click so the inviter shows up in their
  // Ghost Report immediately. iter-32 fallback: when no explicit ?ref= is
  // present, stash the silo's slug as `from_silo` so the silo owner gets
  // organic-acquisition credit at signup time.
  //
  // P0 Feb 7 — also fetch the inviter's display info from the backend
  // resolver so the header pill can show "Invited by <inviter site_name>"
  // rather than the silo owner's name. The actual attribution row is
  // already correct in the DB (record_pending stores it under the inviter's
  // user_id), but the visible label was previously hardcoded to the silo
  // owner — which is exactly what made fans think their share wasn't being
  // attributed.
  const [inviter, setInviter] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get("ref");
        // Oct 2026 (Randy, seeding pass) — remember this visit in the ring
        // REGARDLESS of how they arrived. Signup offers these back as "who
        // sent you?", and a visit through someone's invite link still counts
        // as a silo this person has seen. Unlike `from_silo` this never
        // overwrites — it's a list, newest first, capped at five.
        if (slug) pushVisitedSilo(slug);
        if (ref) {
          setRefSig(ref);
          const visitor_sig = getVisitorSig();
          if (visitor_sig) {
            api.post("/referrals/accept", { referrer_sig: ref, visitor_sig, silo_id: null }).catch(() => {});
          }
          // Resolve inviter site_name for header display. Empty/404 → silently
          // fall back to silo owner's site_name (the existing behaviour).
          try {
            const r = await api.get(`/referrals/resolve/${encodeURIComponent(ref)}`);
            if (!cancel && r.data) setInviter(r.data);
          } catch { /* unknown inviter — leave inviter=null */ }
        } else if (slug) {
          // No explicit ?ref= — stash silo slug so signup can attribute the
          // fan as a referral of the silo's creator. We do NOT overwrite an
          // existing ref_sig (explicit invite link always wins).
          const existingRef = getRefSig();
          if (!existingRef) {
            setFromSilo(slug);
          } else {
            // P0 Feb 7 — visitor came in through someone's invite earlier in
            // the session, then bounced back to this silo via the marketing
            // page or a back-navigation (no URL ?ref= on this hop). Honour
            // the persisted inviter for the header pill so the "INVITED BY"
            // label stays true to the actual referrer instead of silently
            // defaulting back to the silo owner's site_name.
            try {
              const r = await api.get(`/referrals/resolve/${encodeURIComponent(existingRef)}`);
              if (!cancel && r.data) setInviter(r.data);
            } catch { /* unknown inviter — leave inviter=null */ }
          }
        }
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [slug]);

  // Feb 18, 2026 — fire A2HS popup after successful Stripe subscribe so
  // paid subscribers get the same home-screen install nudge as free-subs.
  const [postStripeA2HS, setPostStripeA2HS] = useState(false);

  // iter-22: surface Stripe flow results from the URL so the user sees confirmation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("stripe");
    const sessionId = params.get("session_id");
    // Strip the params first so this can't re-fire on re-render / reload.
    if (flag) {
      params.delete("stripe");
      params.delete("session_id");
      const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", newUrl);
    }
    if (flag === "fan_success") {
      // Jun 18, 2026 — Stripe webhooks are NOT delivered to this origin, so we
      // confirm the subscription on return; otherwise the fan pays but never
      // gets the subscriber row / access. Then reload so access unlocks.
      (async () => {
        try {
          if (sessionId) await api.post("/stripe/fan/confirm", { session_id: sessionId });
        } catch { /* non-fatal — leave it to any future webhook */ }
        toast.success("Subscription active! You now have access to this silo.");
        setPostStripeA2HS(true);
        setTimeout(() => window.location.reload(), 1200);
      })();
    } else if (flag === "tip_success") {
      // Sep 24, 2026 — tips had NO return handler at all: Stripe sent the fan
      // back here, the params were stripped, and nothing was said or recorded.
      // Confirm-on-return now books the tip (the webhook is the other belt)
      // and says thank you out loud. Reload because a tip can reset the dot
      // board / lift the ceiling, which changes what this page allows.
      //
      // The message follows the session's REAL state. An async method (Amazon
      // Pay, Klarna, Cash App, bank) can land the payer back here while the
      // money is still in flight, and thanking them for a payment that may
      // never arrive is worse than saying nothing.
      (async () => {
        let state = "paid";
        try {
          if (sessionId) {
            const r = await api.post("/tips/confirm", { session_id: sessionId });
            state = r.data?.state || (r.data?.recorded ? "paid" : "processing");
          }
        } catch { /* non-fatal — the webhook still books it */ }
        if (state === "paid") {
          toast.success("Tip sent — thank you! The creator has been notified.");
        } else if (state === "processing") {
          toast("Your payment is still clearing — the tip will be credited the moment it settles.", { icon: "⏳" });
        } else if (state === "expired") {
          toast.error("That checkout expired before it was paid — nothing was charged.");
        } else {
          toast("No payment was completed, so nothing was charged.", { icon: "↩" });
        }
        setTimeout(() => window.location.reload(), 1800);
      })();
    } else if (flag === "cancelled") {
      toast("Payment cancelled.", { icon: "↩" });
    }
    // Jun 2026 (Randy) — fan-funded voice return. Webhooks aren't delivered here,
    // so confirm-on-return credits the silo's voice voucher, then reload so
    // premium voice unlocks for everyone.
    const cf = params.get("voice_crowdfund");
    const cfSession = params.get("session_id");
    if (cf) {
      params.delete("voice_crowdfund");
      params.delete("session_id");
      const u = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", u);
      if (cf === "success") {
        (async () => {
          try {
            if (cfSession) await api.post(`/s/${slug}/voice/crowdfund-confirm`, { session_id: cfSession });
            toast.success("Premium voice is switched on — thank you for chipping in! 🎉");
          } catch { toast.error("We couldn't confirm that payment — please contact support if voice stays off."); }
          setTimeout(() => window.location.reload(), 1400);
        })();
      } else if (cf === "cancel") {
        toast("Payment cancelled.", { icon: "↩" });
      }
    }
  }, [slug]);

  const startFanSubscription = async () => {
    setSubscribing(true);
    try {
      // Jul 2026 (Randy, child card guard) — family-restricted accounts get a
      // plain-language heads-up before Stripe: a parent must type the card.
      try {
        const fs = await api.get("/me/family-status");
        if (fs.data?.restricted) {
          const ok = window.confirm(
            "This is a family-restricted account.\n\nAsk your parent first — they'll need to type their card at checkout. Cards are never saved on this account, so every purchase needs a parent present.\n\nContinue to checkout?"
          );
          if (!ok) { setSubscribing(false); return; }
        }
      } catch { /* signed out — carry on */ }
      const r = await api.post("/stripe/fan/checkout", {
        slug,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start subscription.");
      setSubscribing(false);
    }
  };

  // Jun 2026 — Home-Screen launch detection. When the silo is opened as an
  // installed home-screen app (standalone display mode), award that dot.
  useEffect(() => {
    if (!silo) return;
    try {
      const standalone = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
        || window.navigator.standalone === true;
      if (standalone) interactions.award("home_screen");
    } catch { /* no matchMedia */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo?.id]);

  // Jun 24, 2026 — iOS "Add to Home Screen" manifest self-heal. iOS Safari
  // only reads the <link rel="manifest"> present at INITIAL HTML parse and
  // ignores JS-injected changes during SPA routing. So if a visitor reaches
  // this silo via in-app navigation, the page still carries the GLOBAL
  // manifest (start_url="/") and a home-screen icon saved here would open
  // the marketing page signed-out. Fix: when we land on /s/<slug> and the
  // linked manifest isn't this silo's, do ONE full reload so index.html's
  // inline script links the correct per-silo manifest BEFORE the user taps
  // Share → Add to Home Screen. Skipped inside iframes (the Assistant embeds
  // the public page) and in standalone PWA mode; an 8s guard prevents loops.
  useEffect(() => {
    if (!slug) return;
    try {
      if (window.self !== window.top) return;
      const standalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true;
      if (standalone) return;
      const lastReload = Number(sessionStorage.getItem("mysilo_manifest_reload_ts") || 0);
      if (Date.now() - lastReload < 8000) return;
      const link = document.querySelector('link[rel="manifest"]');
      const href = link ? (link.getAttribute("href") || "") : "";
      if (!href.includes(`/s/${slug}/manifest`)) {
        sessionStorage.setItem("mysilo_manifest_reload_ts", String(Date.now()));
        window.location.reload();
      }
    } catch { /* never block render */ }
  }, [slug]);

  // May 24, 2026 — PWA install handoff + every-launch cookie restore moved
  // UP to StandaloneSessionGate in App.js (Oct 2026, pt-launch-target). It had
  // to move above the route guards so an icon pointing at an AUTHED
  // destination (Assistant / Chats / YTRE) isn't bounced to /login before any
  // page effect can run. Keeping a second copy here would have given one
  // mechanism two owners and risked double-redeeming the single-use token.

  // May 31, 2026 — Clean-slate reset whenever the silo slug changes
  // (Discover drawer, global search, magic links, profile links). Client-
  // side navigation between public silos was leaving the PREVIOUS silo's
  // conversation on screen — so a visitor saw creator A's answer sitting
  // under creator B's composer / Subscribe / Tip. Each silo page must be
  // its own separate, clean conversation.
  useEffect(() => {
    let sessionFromUrl = "";
    let bookmarkFromUrl = "";
    try {
      const sp = new URLSearchParams(window.location.search);
      sessionFromUrl = sp.get("session") || "";
      bookmarkFromUrl = sp.get("bookmark") || "";
    } catch { sessionFromUrl = ""; bookmarkFromUrl = ""; }
    // Jun 2026 — a ?bookmark=<id> link overrides everything: we load that saved
    // thread (separate effect) and DON'T restore/hydrate any prior session.
    let restored = "";
    if (!sessionFromUrl && !bookmarkFromUrl) {
      try {
        if (getToken()) restored = localStorage.getItem(`mysilo_chat_session__${slug}`) || "";
      } catch { restored = ""; }
    }
    setMessages([]);
    setInput("");
    setLoadedBookmarkId("");
    setLoadedBookmarkAudio({ complete: false, live: false });
    setTipOpen(false);
    setSubscribePopupOpen(false);
    setReportOpen(false);
    setExportOpen(false);
    setOwnerByokNudge({ open: false, ownerName: "" });
    setSessionId(bookmarkFromUrl ? "" : (sessionFromUrl || restored));
    autoFiredRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Jun 1, 2026 — persist the active conversation id for THIS silo so it can
  // survive the sign-up hop (restored above, for authenticated viewers only).
  // Written for everyone; the gate on re-read keeps anonymous return visits
  // clean-slate per Option B.
  useEffect(() => {
    if (!slug || !sessionId) return;
    try { localStorage.setItem(`mysilo_chat_session__${slug}`, sessionId); } catch { /* ignore */ }
  }, [sessionId, slug]);

  useEffect(() => {
    (async () => {
      try {
        // Jun 2026 (Randy) — a shared-bookmark link can point at an UNLISTED
        // (hidden) silo on purpose. Pass the bookmark id so the backend grants
        // read access to that shared thread even though the silo is unlisted.
        const _bmq = (() => { try { return new URLSearchParams(window.location.search).get("bookmark") || ""; } catch { return ""; } })();
        const _qs = _bmq ? `?bookmark=${encodeURIComponent(_bmq)}` : "";
        const [s, st] = await Promise.all([
          api.get(`/s/${slug}${_qs}`),
          api.get(`/s/${slug}/stats${_qs}`),
        ]);
        setSilo(s.data); setStats(st.data);
        // iter-33: track current_referrer for logged-in non-creators. The
        // backend short-circuits paid creators + super admins. The most
        // recently-visited silo's owner becomes the referrer-of-record at
        // the moment of platform-subscription upgrade.
        try {
          const u = getUser();
          if (u && !u.is_paid_creator && u.role !== "super_admin") {
            api.post("/me/track-referrer", { silo_slug: slug }).catch(() => {});
          }
        } catch { /* ignore */ }
        // Jun 2026 (Randy) — hydrate a SHARED/loaded bookmark (?bookmark=<id>)
        // INSIDE the same async flow as the silo load. Doing it in a separate
        // effect raced the clean-slate reset (StrictMode double-mount), so the
        // thread sometimes loaded blank. Single source of truth = no clobber.
        try {
          const bm = new URLSearchParams(window.location.search).get("bookmark") || "";
          if (bm) {
            const rb = await api.get(`/shared-bookmark/${encodeURIComponent(bm)}`);
            const turns = (rb.data?.messages || []).map((m) => ({
              role: m.role === "assistant" ? "ai" : m.role,
              text: m.text,
              outOfScope: !!m.outOfScope,
            }));
            if (turns.length) setMessages(turns);
            setLoadedBookmarkId(bm);
            setLoadedBookmarkAudio({ complete: !!rb.data?.audio_complete, live: !!rb.data?.audio_live });
            setSessionId("");
            // Jun 2026 (Randy) — re-mint THIS silo's 24h voice cache from the
            // shared bookmark's pre-paid audio so the visitor (anon/fan/creator)
            // gets the full machinery: lit-green per-bubble icons, free stitched
            // replay, a working scrubber, and dots covering only NEW turns they
            // add. Best-effort: if it fails, the pre-paid blob still plays.
            if (turns.length && rb.data?.audio_complete) {
              try {
                const plan = buildBookmarkPlan(turns, answerVoice);
                await api.post(`/shared-bookmark/${encodeURIComponent(bm)}/hydrate-cache`, {
                  segments: plan.map((s) => ({ seg_idx: s.seg_idx, voice: s.voice, chunks: s.chunks })),
                });
                lastProbeSigRef.current = "";
                setHydrateNonce((n) => n + 1);
              } catch { /* hydration best-effort */ }
            }
            // Jun 2026 (Randy) — make a SHARED conversation RECALLABLE. If the viewer
            // is signed in, record the open now; if anonymous (they may sign up next),
            // stash it so we record once they're authenticated back on this silo.
            if (getToken()) {
              api.post(`/shared-bookmark/${encodeURIComponent(bm)}/record-open`).catch(() => {});
            } else {
              try { localStorage.setItem("mysilo_pending_shared_bm", JSON.stringify({ id: bm, slug })); } catch { /* */ }
            }
          }
        } catch { /* unknown/expired bookmark id → stay clean */ }
      } catch (e) {
        if (e.response?.status === 404) {
          // May 24, 2026 — distinguish "really not found" from "hidden,
          // viewer not authorized". If the URL carried a handoff token
          // OR the silo was reached via the home-screen icon (standalone
          // PWA mode), the 404 is almost certainly because the handoff
          // expired/was-consumed before we got here, so the better UX is
          // a "Sign in" prompt instead of the cold "No such silo" page.
          const cameFromHandoff = window.location.search.includes("handoff=")
            || /^\/s\/[^/]+\?.*auto_q=/.test(window.location.pathname + window.location.search);
          if (cameFromHandoff && !getToken()) {
            setNeedsLogin(true);
          } else {
            setNotFound(true);
          }
        } else {
          toast.error("Could not load silo");
        }
      }
    })();
  }, [slug]);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Jun 2026 (Randy) — a shared conversation opened while logged-OUT becomes
  // recallable the moment the now-signed-in member lands back on this silo.
  useEffect(() => {
    if (!silo || !getToken()) return;
    let pend = null;
    try { pend = JSON.parse(localStorage.getItem("mysilo_pending_shared_bm") || "null"); } catch { pend = null; }
    if (pend?.id && pend.slug === slug) {
      api.post(`/shared-bookmark/${encodeURIComponent(pend.id)}/record-open`)
        .catch(() => {})
        .finally(() => { try { localStorage.removeItem("mysilo_pending_shared_bm"); } catch { /* */ } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo, slug]);

  // iter-16: hydrate prior conversation from the server when the user returns
  // to a silo they already chatted with. Anonymous — session_id is the key.
  useEffect(() => {
    if (!sessionId || !silo) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/s/${slug}/chat/${encodeURIComponent(sessionId)}`);
        if (cancel) return;
        const prior = (r.data?.messages || []).map((m) => ({
          role: m.role === "assistant" ? "ai" : m.role,
          text: m.text,
        }));
        if (prior.length) setMessages(prior);
      } catch { /* fresh session is fine */ }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo, slug]);

  // Jun 2026 (Randy) — SHARED/loaded bookmark hydration (?bookmark=<id>) now lives
  // inside the silo-load effect above (single async flow, no cross-effect race).

  // Override the live conversation with a saved bookmark (from the left shelf).
  // Stamps ?bookmark=<id> into the URL so refresh AND share keep this thread.
  // Jun 2026 (Randy) — build the TWO-VOICE plan for a set of saved messages, exactly
  // as live playback reads it (question voice + answer voice as separate clips), so
  // saved/shared audio matches the 24h cache and behaves identically everywhere.
  const buildBookmarkPlan = (msgs, av) => {
    const i = Math.max(0, VOICE_OPTIONS.findIndex((o) => o.id === av));
    const qv = VOICE_OPTIONS[(i + 1) % VOICE_OPTIONS.length].id;
    return (msgs || [])
      .filter((m) => (m.text || "").trim())
      .map((m, idx) => {
        const ai = (m.role === "assistant" || m.role === "ai");
        return { seg_idx: idx, voice: ai ? av : qv, text: m.text, chunks: chunkForSpeech(m.text) };
      });
  };
  // Recall (Jun 2026, Randy) — bring back the signed-in viewer's recent
  // conversations on THIS silo, on any device. Loading one refreshes its 30-day
  // text clock + 24h audio cache (touch). Hidden for anonymous visitors.
  const recallProps = {
    enabled: !!getToken(),
    testidPrefix: "qa-recall",
    fetchRecent: async () => {
      try { const r = await api.get(`/s/${slug}/my-recent-sessions?limit=5`); return r.data?.sessions || []; }
      catch { return []; }
    },
    onLoad: (sess) => {
      const turns = (sess.messages || []).map((m) => ({
        role: (m.role === "assistant" || m.role === "ai") ? "ai" : "user",
        text: m.text,
      }));
      // Randy — open a recalled/saved conversation at the TOP (start), not bottom.
      const toTop = () => setTimeout(() => {
        try { feedRef.current?.scrollTo({ top: 0, behavior: "smooth" }); window.scrollTo({ top: 0 }); } catch { /* */ }
      }, 60);
      if (sess.is_bookmark && sess.bookmark) {
        const bm = sess.bookmark;
        const live = (() => { try { return !!bm.audio_expires_at && new Date(bm.audio_expires_at).getTime() > Date.now(); } catch { return false; } })();
        setLoadedBookmarkId(bm.id);
        setLoadedBookmarkAudio({ complete: !!bm.audio_complete && live, live });
        setMessages(turns);
        setSessionId("");
        toTop();
        return;
      }
      setLoadedBookmarkId("");
      setLoadedBookmarkAudio({ complete: false, live: false });
      setMessages(turns);
      setSessionId(sess.session_id);
      try { localStorage.setItem(`mysilo_chat_session__${slug}`, sess.session_id); } catch { /* */ }
      toTop();
    },
    touch: (sess) => {
      const segs = buildBookmarkPlan(sess.messages || [], answerVoice).map((s) => ({ voice: s.voice, chunks: s.chunks }));
      api.post(`/s/${slug}/recall-touch`, { session_id: sess.session_id, segments: segs }).catch(() => {});
    },
  };

  // Kept in a ref so applyLoadedBookmark (stable useCallback) always calls the
  // freshest closure (with the current voice picker / silo).
  const offerCompleteAudioRef = useRef(null);
  offerCompleteAudioRef.current = async (b) => {
    if (!getToken()) return; // only the signed-in owner can complete it
    const av = selectedVoice || silo?.voice_id || "onyx";
    const segs = buildBookmarkPlan(b.messages, av);
    if (!segs.length) return;
    try {
      // Ask the bookmark's OWN 7-day store (not the ephemeral 24h cache) what's
      // genuinely missing. Self-heals audio_complete too — a fully-listened
      // bookmark stops nagging to re-cache parts it already has saved.
      const cov = await api.post(`/bookmarks/${b.id}/audio-coverage`, {
        segments: segs.map((s) => ({ seg_idx: s.seg_idx, chunk_count: s.chunks.length })),
      });
      if (cov.data?.audio_complete) {
        setLoadedBookmarkAudio({ complete: true, live: true });
        return; // nothing missing → no popup
      }
      const missSet = new Set(cov.data?.missing_seg_idxs || []);
      const missSegs = segs.filter((s) => missSet.has(s.seg_idx));
      if (missSegs.length) {
        const cents = missSegs.reduce((n, s) => n + s.text.replace(/\s+/g, " ").trim().length * 0.0015, 0);
        setCompleteAudioPopup({ bmId: b.id, segs, missSegIdxs: [...missSet], missCount: missSegs.length, cents });
      }
    } catch { /* coverage check failed → don't nag */ }
  };
  const completeBookmarkAudio = async () => {
    if (!completeAudioPopup) return;
    setCompletingAudio(true);
    const { bmId, segs, missSegIdxs } = completeAudioPopup;
    const missSet = new Set(missSegIdxs || []);
    const toWarm = segs.filter((s) => missSet.has(s.seg_idx));
    try {
      for (const s of toWarm) { try { await voice.warmCached(s.text, s.voice); } catch { /* */ } }
      // attach-audio MERGES — it keeps every chunk already in the 7-day store and
      // adds the freshly-warmed ones, so completing gaps never wipes prior audio.
      const r = await api.post(`/bookmarks/${bmId}/attach-audio`, {
        segments: segs.map((s) => ({ seg_idx: s.seg_idx, voice: s.voice, chunks: s.chunks })),
      });
      setLoadedBookmarkAudio({ complete: !!r.data?.audio_complete, live: !!r.data?.has_audio });
      toast.success(r.data?.audio_complete ? "Audio completed — it now plays free for anyone you share it with." : "Saved the audio we could — add an OpenAI key/voucher to finish the rest.");
    } catch { toast.error("Couldn't complete the audio."); }
    finally { setCompletingAudio(false); setCompleteAudioPopup(null); }
  };
  const applyLoadedBookmark = useCallback((b) => {
    const turns = (b.messages || []).map((m) => ({
      role: m.role === "assistant" ? "ai" : m.role,
      text: m.text,
      outOfScope: !!m.outOfScope,
    }));
    setMessages(turns);
    setSessionId("");
    setLoadedBookmarkId(b.id || "");
    // Randy — open a loaded bookmark at the TOP (start), not the bottom.
    setTimeout(() => { try { feedRef.current?.scrollTo({ top: 0, behavior: "smooth" }); window.scrollTo({ top: 0 }); } catch { /* */ } }, 60);
    // Reflect the bookmark's audio status (from the user's own shelf row).
    const live = (() => {
      try { return !!b.has_audio && !!b.audio_expires_at && new Date(b.audio_expires_at).getTime() > Date.now(); }
      catch { return false; }
    })();
    setLoadedBookmarkAudio({ complete: !!b.audio_complete && live, live });
    // OPEN-TIME completion CTA — only when this owns-bookmark has PARTIAL audio.
    if (b.id && b.has_audio && !b.audio_complete && live) {
      try { offerCompleteAudioRef.current?.(b); } catch { /* */ }
    }
    try {
      const u = new URL(window.location.href);
      if (b.id) u.searchParams.set("bookmark", b.id); else u.searchParams.delete("bookmark");
      u.searchParams.delete("session");
      window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    } catch { /* non-fatal */ }
  }, []);
  // apple-touch-icon, apple-mobile-web-app-title, and per-silo PWA
  // manifest so "Add to Home Screen" saves a silo-specific icon that
  // launches DIRECTLY into this silo's public Q&A. Reusable hook now;
  // SiloManage uses the same hook to make the icon work from creator
  // contexts (Library, Workbench, Refinery, Staging) too.
  useSiloManifest(silo);

  const clearConversation = async () => {
    // Jun 2026 (Randy) — "Clear" is a RESET, not a delete. It clears the on-screen
    // conversation and starts fresh; the conversation stays saved server-side (the
    // creator still sees it in their Chats tab). It is NOT deleted from the database.
    try { localStorage.removeItem(`mysilo_chat_session__${slug}`); } catch { /* ignore */ }
    try { localStorage.removeItem(`public_chat__${slug}`); } catch { /* ignore */ }
    setSessionId("");
    setMessages([]);
    toast.success("Started fresh.");
  };

  // Feb 22, 2026 — auto-fire question from a Staging push deep-link.
  // Declared above the early-return guards to keep Rules of Hooks honored.
  // Uses askRef so the (declared-later) `ask` closure is reachable.
  //
  // Feb 23, 2026 — when the URL is `?auto_q=__random__` (or `?ping=1`),
  // fetch a random question from the silo's pooled bank instead of using
  // a literal string. Powers "tap home-screen icon → random question" UX.
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (!silo) return;
    const params = new URLSearchParams(window.location.search);
    let autoQ = params.get("auto_q");
    const wantRandom = params.get("ping") === "1" || autoQ === "__random__";
    if (!autoQ && !wantRandom) return;
    autoFiredRef.current = true;
    // Strip query params so refresh/back-button doesn't re-trigger.
    params.delete("auto_q");
    params.delete("ping");
    const cleanQS = params.toString();
    const cleanURL = window.location.pathname + (cleanQS ? "?" + cleanQS : "") + window.location.hash;
    window.history.replaceState({}, "", cleanURL);

    (async () => {
      let q = wantRandom ? null : autoQ;
      if (wantRandom) {
        try {
          const r = await api.get(`/s/${slug}/random-bank-question`);
          q = r.data?.question || null;
        } catch { /* fall through — silo just won't auto-ask */ }
        if (!q) {
          // Silo has no bank questions yet — silently no-op. Used to fire a
          // toast suggesting the user add questions on the Staging tab, but
          // that surfaced on every public visitor's home-screen tap too,
          // which was confusing for fans. May 24, 2026 — removed toast.
          return;
        }
      }
      if (!q) return;
      setInput(q);
      setTimeout(() => askRef.current && askRef.current(q), 50);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo]);

  if (needsLogin) {
    // May 24, 2026 — Hidden silo + expired handoff. Tell the user clearly
    // why they can't see this silo and offer a one-tap path to sign in
    // and return here. Strip the (now-stale) ?handoff= param from the
    // return URL so the second visit isn't trying to re-exchange a
    // dead token.
    const cleanPath = window.location.pathname;
    const returnTo = encodeURIComponent(cleanPath);
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] px-6 text-center">
        <div className="max-w-sm">
          <div className="font-serif-display text-4xl text-[#1C1B1A]">Sign in to view this silo.</div>
          <p className="text-[#6B6A68] mt-3 leading-relaxed">
            This is a private silo — only its owner and collaborators can open it.
            The auto sign-in from your home-screen icon expired, so you'll need to log in once.
          </p>
          <Link
            to={`/login?next=${returnTo}`}
            data-testid="needs-login-signin-btn"
            className="mt-6 inline-block text-sm px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8]"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] px-6 text-center">
        <div>
          <div className="font-serif-display text-5xl text-[#1C1B1A]">No such silo.</div>
          <p className="text-[#6B6A68] mt-2">The link <code className="font-mono">/s/{slug}</code> doesn't lead anywhere.</p>
          <Link to="/" className="mt-6 inline-block text-sm px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8]">Go home</Link>
        </div>
      </div>
    );
  }
  if (!silo) {
    return <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68]">Loading…</div>;
  }

  // Feb 22, 2026 — deep-link auto-fire support.
  // When a Staging push notification is tapped, the URL is built like
  //   /s/<slug>?auto_q=<encoded question>
  // We detect the param ONCE on first render after silo is loaded, fire
  // the question, then strip the param so a refresh doesn't re-trigger.
  // Lives here (after early-return on !silo) so `ask` is in scope.

  const ask = async (text, { useTokens = false } = {}) => {
    let answerText = null;
    const q = (text ?? input).trim();
    if (!q || loading) return;
    // Iter171 (Randy pt-out-of-dots-preserve-question, Feb 13 2026) —
    // Do NOT clear the input yet. If the server 402s with a paywall
    // (OUT_OF_INTERACTIONS, CEILING_EXCEEDED, OWNER_BYOK_NOT_SET, etc.)
    // and pops a CTA, the user's typed question must still be sitting
    // in the box when they close the popup. `setInput("")` now fires
    // ONLY on the SUCCESS branch below (after res.data.answer arrives).
    setMessages((m) => [...m, { role: "user", text: q }]);
    // Glide the just-posted question to the top of the screen (Randy, Jul 2026).
    scrollQuestionToTop(messages.length);
    setLoading(true);
    try {
      const res = await api.post(`/s/${slug}/ask`, {
        question: q,
        session_id: sessionId || undefined,
        // Feb 14, 2026 — visitor's chosen answer length. Server persists
        // it on the chat session so subsequent turns inherit it.
        answer_style_preset: answerStyle.preset || undefined,
        answer_style_custom: answerStyle.custom || undefined,
        // Jun 2026 — a paid subscriber past their ceiling opts into spending
        // their interaction tokens instead of tipping.
        use_tokens: useTokens || undefined,
        // Iter160e (Randy) — session-only LLM surface picker override.
        // Empty (spreads to nothing) when the visitor hasn't picked one.
        ...overrideAsAskBody("public_ask"),
      });
      answerText = res.data.answer;
      // Success — the answer landed, so we can safely clear the input now.
      setInput("");
      setMessages((m) => [...m, { role: "ai", text: res.data.answer, outOfScope: res.data.out_of_scope }]);
      // Capture the server-assigned session_id so subsequent turns in THIS
      // conversation thread it correctly. Jun 1, 2026 — this id is also stashed
      // to localStorage (effect above) so it survives a sign-up; anonymous
      // return visits still start fresh (only logged-in viewers restore it).
      if (res.data.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
      }
      // Jun 2026 (Randy) — count this anon's own questions to time the
      // "keep this conversation" sign-up nudge (logged-in viewers don't need it).
      if (!getToken()) setAnonAskCount((n) => n + 1);
      // Jun 2026 — patch the dots board from the answer (no extra round-trip).
      if (res.data.interactions) interactions.applyFromAsk(res.data.interactions);
    } catch (e) {
      // api.js stringifies error.response.data.detail for safe React render,
      // so read the structured code from the stable `guard_code` / `detail_obj`
      // fields it preserves (NOT detail.code, which is now a JSON string).
      const guardCode = e.response?.data?.guard_code;
      const detailObj = e.response?.data?.detail_obj || {};
      // Jun 2026 — Visitor billing walls. These fire BEFORE the generic 402
      // paywall branch. Remove the optimistic user bubble (no answer came) and
      // pop the right popup. The board snapshot rides along in the payload.
      if (e.response?.status === 402 && guardCode === "OUT_OF_INTERACTIONS") {
        setMessages((m) => m.slice(0, -1));
        if (detailObj.interactions) interactions.applyFromAsk(detailObj.interactions);
        if (!seenUnlockIntroRef.current) {
          seenUnlockIntroRef.current = true;
          setQ4Open(true);              // first time → the welcome / open-drawer nudge
        } else {
          setOutOfDotsCtaOpen(true);    // after that → unified 3-ways out-of-dots CTA
        }
        return;
      }
      if (e.response?.status === 402 && guardCode === "CEILING_EXCEEDED") {
        setMessages((m) => m.slice(0, -1));
        if (detailObj.interactions) interactions.applyFromAsk(detailObj.interactions);
        pendingTokenQRef.current = q;   // remember the question for "use my tokens"
        setCeilingWallOpen(true);
        return;
      }
      // Sprint Feb 8, 2026 — 402 OWNER_BYOK_NOT_SET = silo owner hasn't
      // connected their LLM key yet. Visitor gets the friendly nudge modal;
      // the owner (viewing their own silo) gets an actionable message instead.
      if (e.response?.status === 402 && guardCode === "OWNER_BYOK_NOT_SET") {
        if (silo.viewer_is_owner) {
          setMessages((m) => [...m, {
            role: "ai", error: true,
            text: `**Your silo doesn't have an AI key connected yet.** Add your own key — or top up the platform bucket — in Settings so your silo can start answering.`,
          }]);
        } else {
          setOwnerByokNudge({ open: true, ownerName: detailObj.silo_owner_name || silo.name });
        }
        return;
      }
      // May 28, 2026 — 402 LLM_GUARD = silo owner has no BYOK for the
      // resolved provider AND that provider's voucher bucket is $0.
      if (e.response?.status === 402 && guardCode === "LLM_GUARD") {
        // Jun 5, 2026 — split by viewer. Owner gets an actionable nudge; a
        // visitor sees a calm "temporarily down" message and we auto-alert
        // the owner via the existing failure-banner pipeline.
        if (silo.viewer_is_owner) {
          setMessages((m) => [...m, {
            role: "ai", error: true,
            text: `**Your silo's AI engine isn't set up to answer this.** You don't have a key for this provider and the platform bucket is empty — add your key or top up in Settings.`,
          }]);
        } else {
          api.post(`/s/${slug}/alert-owner`).catch(() => {});
          setMessages((m) => [...m, {
            role: "ai", error: true,
            text: `**${silo.name || "This silo"} is temporarily down.** The creator's AI engine isn't responding right now — we've let them know. Please check back in a little while.`,
          }]);
        }
        return;
      }
      // Oct 2026 (TRIAL_PLAN.md §4.5 · §18.1) — the creator's membership has
      // ended. NEVER the subscribe CTA below: a resting silo is not a sales
      // moment, and the person asking may already be paying for it. The
      // server's message is already written for a visitor, so show it as-is.
      const restingCode = guardCode || detailObj.code;
      if (restingCode === "CREATOR_RESTING" || restingCode === "CREATOR_RESTING_UNFUNDED") {
        setMessages((m) => [...m, {
          role: "ai", error: true,
          text: detailObj.message
            || `**${silo.name || "This silo"} is resting.** New answers are paused for now — nothing has been deleted.`,
        }]);
        return;
      }
      // iter-23: 402 means the silo is gated and caller isn't subscribed — show the
      // subscribe CTA inline instead of a generic error bubble.
      if (e.response?.status === 402) {
        const d = e.response?.data?.detail || {};
        const price = Math.round((d.subscriber_price_cents || silo.subscriber_price_cents || 0) / 100);
        setMessages((m) => [...m, {
          role: "ai",
          text: `**Subscribe to unlock answers.** This silo requires a ${price ? `$${price}/month ` : ""}subscription to continue.`,
          paywall: true,
        }]);
        toast.error("Subscription required to chat with this silo.");
        return;
      }
      // iter-26: 503 = creator out of credits → message bubble explains it
      if (e.response?.status === 503) {
        const d = e.response?.data?.detail || {};
        setMessages((m) => [...m, {
          role: "ai", error: true,
          text: `This silo's creator has run out of API credits. ${d.creator_email ? `You can let them know at ${d.creator_email}.` : ""} Check back soon!`,
        }]);
        return;
      }
      if (e.response?.status === 502) {
        // 502 = the creator's AI key threw an error — most often an
        // invalid/expired key. Owner gets an actionable nudge that teaches
        // the difference between "invalid key" and "out of funds"; a visitor
        // sees a calm "temporarily down" and we auto-alert the owner.
        if (silo.viewer_is_owner) {
          setMessages((m) => [...m, {
            role: "ai", error: true,
            text: `**That didn't go through.** Try again in a few seconds. If it keeps happening, you may want to evaluate replacing your AI key in Settings.`,
          }]);
        } else {
          api.post(`/s/${slug}/alert-owner`).catch(() => {});
          setMessages((m) => [...m, {
            role: "ai", error: true,
            text: `**${silo.name || "This silo"} is temporarily down.** Its AI engine isn't responding right now — we've let the creator know. Please check back soon.`,
          }]);
        }
        return;
      }
      const msg = "Something went wrong contacting the assistant. Please try again.";
      setMessages((m) => [...m, { role: "ai", text: msg, error: true }]);
    } finally { setLoading(false); }
    return answerText;
  };

  // Keep the askRef pointed at the latest `ask` closure so the
  // hoisted auto-fire useEffect (above the early returns) can reach it.
  askRef.current = ask;

  // Rolling history compression — when raw transcript exceeds 10,000 chars,
  // compress prior turns into a 1k-char "Context Bridge" so the active prompt stays lean.
  // Composer key handling: Feb 2026 (Randy) — Enter NEVER submits on any
  // surface (mobile or desktop). It inserts a newline, matching iOS Notes /
  // WhatsApp behavior. Send is exclusively via the send button so users can
  // freely format their questions with paragraph breaks. Shift+Enter also
  // stays as a newline (default textarea behavior with no intercept).
  const onKeyDown = () => {};

  // Pick one composer placeholder at random per page load so returning fans see
  // visual variety. Falls back to a sensible default before silo data hydrates.
  const placeholder = (() => {
    const arr = silo?.composer_placeholders;
    if (Array.isArray(arr) && arr.length > 0) {
      return arr[Math.floor(Math.random() * arr.length)];
    }
    return silo ? `Ask ${silo.name} a question…` : "Ask a question…";
  })();

  return (
    <div data-template={silo.template} className="relative isolate" style={{ minHeight: "100dvh" }} data-testid="public-silo-page">
      {/* Feb 17, 2026 (Randy pt-feature-page-model-b) — the backend's
          `silo.silo_art_v2` flag is now derived server-side to include V1
          dashboard mode too (canvas paints for both V1 and V2, subject to
          per-page opt-out overrides). The frontend gate stays a simple
          bool read. */}
      {silo.silo_art_v2 && <SiloArtCanvasFor silo={silo} face="public-qa" pageKey="public_qa" />}
      {/* Sprint Feb 8, 2026 — the IdentityPill and SiloActionsDrawer are
          mounted INLINE in the header's right side (see <header> below)
          so they share flex space with other buttons and never overlap. */}
      {/* Header */}
      {/* May 24, 2026 — iOS PWA safe-area fix. When launched standalone from
          the home-screen icon, iOS doesn't draw its own Safari chrome but
          STILL reserves the status-bar strip at the top. Without the
          env(safe-area-inset-top) padding, the Home button + IdentityPill
          were tucked under the time/battery icons and unclickable.
          Non-sticky, so handled inline (the global CSS rule only targets
          sticky-top headers). */}
      <header
        ref={headerRef}
        className="sticky top-0 z-40 border-b tpl-border pt-[env(safe-area-inset-top)] backdrop-blur-md"
        style={{ background: "color-mix(in srgb, var(--tpl-bg) 92%, transparent)" }}
      >
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between gap-3">
          {/* Shared top-left nav (mobile hamburger + desktop Home). Extracted to
              SiloHeaderNav so the AI Assistant page stays in lockstep with it. */}
          <SiloHeaderNav
            onHome={goHome}
            homeTestid="public-silo-back"
            recall={recallProps}
            tabsToggle
            items={[
              { label: "Clear", icon: <Trash size={14} weight="duotone" />, onClick: () => setClearConfirmOpen(true), testid: "mobile-menu-clear", show: messages.length > 0 },
              { label: "Export", icon: <Export size={14} weight="duotone" />, onClick: () => setExportOpen(true), testid: "mobile-menu-export", show: silo.allow_export && messages.length > 0 },
            ]}
            footerItems={[
              { label: "Report this silo", onClick: () => setReportOpen(true), testid: "tabs-menu-report" },
              { label: `💛 Tip ${silo.name}`, onClick: () => setTipOpen(true), testid: "tabs-menu-tip", show: tabsOff && showTip },
              { label: `Subscribe · ${subPriceLabel}`, onClick: onSubscribePillClick, testid: "tabs-menu-subscribe", show: tabsOff && showSubscribe },
            ]}
          />
          <Link to="/" className="flex items-center gap-3 min-w-0 flex-1" data-testid="public-silo-logo-home">
            <div className="w-9 h-9 rounded-full tpl-accent-bg flex items-center justify-center shrink-0">
              <Sparkle size={18} weight="duotone" />
            </div>
            <div className="min-w-0">
              <div className="tpl-display text-xl leading-none truncate">{silo.name}</div>
              {silo.tagline && <div className="text-[10px] tpl-muted tracking-[0.25em] uppercase mt-1 truncate">{silo.tagline}</div>}
              {silo.silo_kind === "personal" && (
                <div
                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-[#F0F1EE] text-[#4A5D4E] text-[10px] font-semibold uppercase tracking-[0.14em]"
                  data-testid="private-qa-badge"
                  title="Personal silo — private forever. Only you (and invited collaborators) can see this page."
                >
                  Private Q&amp;A
                </div>
              )}
              {silo.voice_crowdfund_count > 0 && (
                <div
                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-[#FDF1ED] text-[#C16E5A] text-[10px] font-semibold"
                  data-testid="voice-crowdfund-badge"
                  title="Fans chipped in to switch on this silo's premium voice"
                >
                  🔥 {silo.voice_crowdfund_count === 1 ? "A fan funded" : `${silo.voice_crowdfund_count} fans funded`} this silo's voice
                </div>
              )}
            </div>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            {messages.length > 0 && (
              <button onClick={() => setClearConfirmOpen(true)} data-testid="clear-conversation-btn"
                className="hidden sm:flex text-[11px] tpl-muted hover:tpl-accent items-center gap-1 px-2 py-1 rounded-md border tpl-border tpl-surface"
                title="Clear this conversation and start fresh">
                Clear
              </button>
            )}
            {silo.allow_export && messages.length > 0 && (
              <button onClick={() => setExportOpen(true)} data-testid="export-open-button"
                className="hidden sm:flex text-[11px] tpl-muted hover:tpl-accent items-center gap-1 px-2 py-1 rounded-md border tpl-border tpl-surface">
                <Export size={12} weight="duotone" /> Export
              </button>
            )}
            {/* Feb 17, 2026 — Star (favorite) button. Logged-in users save the
                silo to their "Your interactions" list. Anons get a sign-up
                nudge instead. Compact mode (icon-only) so it stays unobtrusive. */}
            <StarSiloButton
              siloId={silo.id}
              siloSlug={silo.slug}
              siloName={silo.name}
              signedIn={!!viewer.user}
              compact
              ghostStarred={ghostStarred}
              onGhostToggle={toggleGhostStar}
              onChange={() => interactions.reload()}
            />
            {/* Sprint Feb 8, 2026 — authed viewers get an inline IdentityPill
                + (when viewing another operator's silo) the SiloActionsDrawer.
                Both live in the header's right side, NOT as fixed overlays.
                Anonymous viewers still get the minimal Sign Up CTA below. */}
            {viewer.user && (
              <div className="flex items-center gap-2 ml-2">
                {/* Jul 2026 (Randy) — removed the unused "Silo actions" launcher
                    pill (War / Collaborate / Apprentice, all "soon"). Silo Wars
                    itself is unchanged; only this arbitrary access point is gone. */}
                <BattleInboxBadge />
                <IdentityPill siloId={silo.owner_user_id === viewer?.user?.id ? silo.id : null} siloSlug={silo.owner_user_id === viewer?.user?.id ? silo.slug : null} />
              </div>
            )}
            {(() => {
              if (!silo.owner_name) return null;
              if (viewer.user) return null;
              const inviterLabel = (
                (inviter && (inviter.site_name || inviter.display_name)) ||
                silo.owner_site_name ||
                silo.owner_name ||
                ""
              ).trim();
              return (
                <div className="flex flex-col items-end gap-0.5 ml-1" data-testid="public-silo-shared-by">
                  <button
                    onClick={() => { setFavCtaStar(false); setFavCtaOpen(true); }}
                    data-testid="public-silo-shared-by-signup"
                    className="text-xs px-2.5 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1 shrink-0"
                    title={`Sign up — stay right here. Your invitation seed (${inviterLabel}) carries through.`}
                  >
                    Sign up
                  </button>
                  <span className="text-[8px] sm:text-[10px] tpl-muted tracking-[0.18em] uppercase pr-1 text-right whitespace-nowrap">
                    Invited by <strong className="tpl-text" data-testid="public-silo-inviter-name">{inviterLabel}</strong>
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      </header>

      {/* Top "fan subscribe banner" REMOVED Jun 8, 2026 (Randy) — it duplicated
          the Subscribe pill under the composer AND promised "unlimited" (no longer
          true with the dots/ceiling system). The pill below + the Q3→Q4 sign-up
          CTA are the single subscribe/sign-up doors now. */}

      {/* Oct 2026 (TRIAL_PLAN.md §8.10) — the creator's subscription ended and
          the 30-day grace is up: a quiet resting state instead of an ask box
          that would only 403. Anyone who paid through a later date never gets
          here (D39) — the server keeps serving them. */}
      {silo.resting && (
        <SiloRestingBand siloName={silo.name} onTip={() => setTipOpen(true)} />
      )}

      {/* Hero (when no messages). Jul 2026 (Randy) — owner canvas controls:
          the whole text group can be resized, forced black/white or hidden. */}
      {messages.length === 0 && !silo.resting && (() => {
        const qaFx = silo.silo_art_v2 ? ((normalizeZones(silo.silo_art).page_fx || {}).public_qa || {}) : {};
        if (qaFx.text_hidden) return null;
        const colorCls = qaFx.text_color === "black" ? "siloart-text-black" : qaFx.text_color === "white" ? "siloart-text-white" : "";
        return (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className={`siloart-copy max-w-3xl mx-auto px-6 pt-16 pb-6 text-center ${colorCls}`}>
          <ScaledGroup scale={typeof qaFx.text_scale === "number" ? qaFx.text_scale / 100 : 1}>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] tpl-muted mb-6">
            <BookOpenText size={16} weight="duotone" /> Closed-corpus assistant
          </div>
          <h1 className="tpl-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Ask anything <em className="tpl-accent">{silo.name}</em> would answer.
          </h1>
          <p className="mt-6 text-xs uppercase tracking-[0.25em] tpl-muted leading-relaxed max-w-xl mx-auto">
            Answers come exclusively from this silo's curated corpus. Questions outside its scope are politely declined.
          </p>
          <div className="mt-8 text-sm tpl-muted" data-testid="silo-stats">
            {stats.document_count} source{stats.document_count === 1 ? "" : "s"} · {stats.total_characters.toLocaleString()} characters
          </div>
          </ScaledGroup>
        </motion.section>
        );
      })()}

      {/* Battle Highlights removed Feb 14, 2026 PM — per user request,
          battle stats no longer surface on Public Q&A. The card lived here:
          <BattleHighlights siloId={silo.id} />
          Component file still exists at /features/battles/BattleHighlights.jsx
          in case we want to repurpose it (e.g., creator-only Battle Hub). */}

      {/* Feed */}
      {/* Feb 24 2026 (Randy pt-composer-whitebox-fix) — Hypothesis 2
          (swap 100vh → 100dvh) regressed the keyboard-cycle behavior on
          iOS. Reverted per happy-state bookmark in
          /app/memory/COMPOSER_WHITEBOX_HAPPY_STATE.md. If a future
          attempt wants to touch main's viewport unit, test on iOS FIRST,
          not just Playwright. */}
      <main className="siloart-copy" ref={feedRef} style={{ minHeight: "calc(100vh - 240px)" }}>
        {/* Jul 2026 (Randy) — "Listen to whole conversation" shelf restored as a
            SOLID sticky freeze pane that mirrors the AI Assistant: a thin clear
            bar when minimized, its own solid shelf LOCKED directly under the main
            header when open, with the Q&A scrolling cleanly behind it. It sits as
            a direct child of <main> (full width) and pins at top:headerH. The Q&A
            feed below is unchanged — same silo connection, same ask flow. */}
        {silo.voice_enabled && messages.some((m) => (m.text || "").trim() && !m.error) && (() => {
            const turns = allTurns;
            const speaking = voice.state === "speaking" || voice.state === "processing" || voice.state === "paused";
            const paused = voice.state === "paused";
            // Jun 2026 (Randy) — cost reflects only the MISSING parts (what isn't
            // already cached for free), so re-asking only charges for the new turn.
            const missCents = coverage.missingChars * 0.0015; // 1× raw tts-1 estimate
            const costLabel = missCents < 1 ? `${missCents.toFixed(1)}¢` : `${Math.round(missCents)}¢`;
            const fullyCached = coverage.allCached && turns.length > 0;
            const subscribed = interactions.state?.mode === "dollar";
            const premiumAvailable = !!(silo.voice_enabled && silo.voice_ready);
            const speak = (mode) => voice.speakConversation(turns, { answerVoice, questionVoice, mode, key: "scrub" });
            // 2+ Q+A pairs → ask "from the beginning, or just the latest?".
            const startWhole = (mode) => {
              lastModeRef.current = mode;
              if (qaSegments.length >= 2) { pendingPlayModeRef.current = mode; setPlayChoiceOpen(true); return; }
              speak(mode);
            };
            // Jun 2026 (Randy) — a shared/loaded conversation whose audio is COMPLETE
            // + still alive plays FREE for everyone (the sender pre-paid). The LEFT
            // premium button turns BRIGHT-GREEN "Pre-paid" and streams the saved
            // 7-day audio; no key, no charge, no role gate.
            const prepaid = !!loadedBookmarkId && loadedBookmarkAudio.complete && loadedBookmarkAudio.live;
            // Jun 2026 (Randy) — the pill must follow CURRENT coverage, not the
            // stale whole-convo prepaid flag. With cache hydration working, the
            // shared convo's turns show as fullyCached (green/free) and a NEW
            // uncached turn correctly flips the pill to the dot/premium state.
            // blobFallback only kicks in if hydration FAILED (nothing cached) —
            // then we still let the original pre-paid blob play free.
            const blobFallback = prepaid && coverage.cached.length === 0;
            const playPrepaid = async () => {
              if (speaking) { voice.pauseOrResume(); return; }
              try {
                const r = await api.get(`/shared-bookmark/${encodeURIComponent(loadedBookmarkId)}/audio`);
                const b64 = (r.data?.chunks || []).map((c) => c.audio_base64).filter(Boolean);
                if (!b64.length) {
                  setLoadedBookmarkAudio({ complete: false, live: false });
                  toast.error("This shared audio is no longer available.");
                  return;
                }
                voice.playPrefetched(b64, { key: "all:prepaid" });
              } catch { toast.error("Couldn't load the shared audio."); }
            };
            // Jun 2026 (Randy) — when the whole thread is already cached (incl. a
            // shared convo whose pre-paid audio we re-minted into the cache on
            // load), play it FREE for EVERY visitor via the stitched cache-only
            // replay — same path as the per-bubble green replay, so the SCRUBBER
            // tracks position (the old pre-paid blob player did not).
            const playWholeCached = () => {
              if (speaking) { voice.pauseOrResume(); return; }
              lastModeRef.current = "sponsored";
              voice.speakCached(
                turns.map((t) => ({ role: t.role, text: t.text, idx: t.idx, voice: t.role === "user" ? questionVoice : answerVoice })),
                { key: "scrub" },
              );
            };
            const onLeft = () => {
              if (speaking) { voice.pauseOrResume(); return; }
              if (viewer.isAnon) { setFavCtaStar(false); setFavCtaOpen(true); return; }
              if (!viewer.isOperator) { setBecomeCreatorCtaOpen(true); return; }
              startWhole("self");
            };
            const onCenter = () => {
              if (speaking) { voice.pauseOrResume(); return; }
              // FREE lane → gesture-safe device-voice queue (fixes iOS "no sound"
              // on the whole-conversation read). Questions higher, answers lower.
              voice.speakDevice(turns.map((t) => ({ text: t.text, pitch: t.role === "user" ? 1.18 : 0.92, idx: t.idx })));
            };
            const onRight = () => {
              if (speaking) { voice.pauseOrResume(); return; }
              if (!premiumAvailable) return;
              if (!subscribed) { if (getToken()) setSubscribePopupOpen(true); else { setFavCtaStar(false); setFavCtaOpen(true); } return; }
              startWhole("sponsored");
            };
            // Jun 2026 (Randy) — the "Dot" listen state. A logged-in FAN visiting a
            // public silo in DOTS mode can hear the whole conversation in premium
            // voice by spending earned dots: 1 dot covers up to 5¢ of the MISSING
            // (not-yet-voiced) cost, rounded up. The dots are debited server-side,
            // then the owner's funding pays the synthesis (sponsored) — once voiced
            // it's cached, so replays are free (the green "Cached" state takes over).
            // Not enough dots → the "Listen in premium — on your own key" CTA.
            const dotsAvailable = interactions.state?.available || 0;
            const dotCost = missCents > 0 ? Math.max(1, Math.ceil(missCents / 5)) : 0;
            // Jun 2026 (Randy) — ANY non-owner visitor in dots mode (fan,
            // creator, or affiliate) can spend dots to hear premium voice — not
            // just fans. Anon also see the green offer but their click routes to
            // the sign-up CTA (their dots aren't account-bound for voice yet).
            // mode==="dots" already excludes owners (unlimited) and subscribers
            // (dollar), so this is purely a visitor surface.
            const showDotState = interactions.state?.mode === "dots"
              && dotsAvailable > 0 && premiumAvailable && !fullyCached && dotCost > 0;
            const onDotSpend = async () => {
              if (speaking) { voice.pauseOrResume(); return; }
              // Jun 2026 (Randy) — anon CAN now spend dots on premium voice (their
              // dots are honored server-side, bounded by the per-IP/silo abuse caps).
              if (dotsAvailable < dotCost) {
                // A creator/affiliate visitor who's out of dots can still
                // self-pay; a fan gets the become-creator nudge.
                if (viewer.isOperator) { startWhole("self"); return; }
                setOutOfDotsCtaOpen(true); return;
              }
              try {
                const r = await api.post(`/s/${slug}/voice/dot-spend`, { dots: dotCost });
                if (r.data?.interactions) interactions.applyFromAsk(r.data.interactions);
              } catch (e) {
                const det = e?.response?.data?.detail;
                if (det && typeof det === "object" && det.interactions) interactions.applyFromAsk(det.interactions);
                if (det?.code === "NOT_ENOUGH_DOTS") { setOutOfDotsCtaOpen(true); return; }
                toast.error("Couldn't start dot playback — please try again.");
                return;
              }
              lastModeRef.current = "sponsored";
              speak("sponsored");
            };
            const iconSize = 22;
            const tipText = "On your iPhone: Settings → Accessibility → Spoken Content → read and speak → Voices → English → pick a voice and tap to download the \"Enhanced\" or \"Premium\" version (e.g., Samantha (Enhanced)).";
            const leftTipText = "If listening to the premium Voice is your thing (it is mine) and you prefer not to subscribe to the silo just yet…this option allows you to pay the pennies it costs to enjoy premium Voice. Note: Sometimes a 10 to 15 second warm-up can ensue. (OpenAI is the LLM surface used)";
            const rightTipText = "Subscribe to this silo! You'll find that the owner covers the premium Voice package for you to an extent. You should thank them.";
            // Consistent little "eye" info bubble used on every lane.
            const eyeTip = ({ open, setOpen, title, body, toggleTestid, popTestid, align = "center" }) => {
              const popPos = align === "left" ? "left-0" : align === "right" ? "right-0" : "left-1/2 -translate-x-1/2";
              return (
                <div className="absolute" style={{ bottom: 12, left: 72 }}>
                  <button
                    type="button"
                    data-testid={toggleTestid}
                    aria-label={title || "More info"}
                    onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                    className="w-4 h-4 rounded-full flex items-center justify-center tpl-surface border tpl-border shadow-sm hover:scale-110 transition-transform"
                    style={{ color: "var(--tpl-muted, #6B6A68)" }}
                  >
                    <Eye size={10} weight="duotone" />
                  </button>
                  {open && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid={`${toggleTestid}-backdrop`} />
                      <div
                        data-testid={popTestid}
                        className={`absolute z-50 ${popPos} top-6 w-56 tpl-surface border tpl-border rounded-lg shadow-xl p-3 text-[11px] leading-snug text-left`}
                        style={{ color: "var(--tpl-text, #1C1B1A)" }}
                      >
                        {title && <div className="font-semibold mb-1">{title}</div>}
                        {body}
                      </div>
                    </>
                  )}
                </div>
              );
            };
            // Jul 2026 (Randy) — CAROUSEL modality: show ONE listen option at a
            // time with left/right arrows (+ swipe) instead of 2-3 discs crowding
            // the row. Order left→right: Free · [default] You-pay · Silo-pays. A
            // hidden premier slot drops in here later without a redesign.
            const laneNodes = [
              // index 0 — FREE device voice (LEFT)
              <CurvedListenButton
                key="free"
                id="center"
                testid="listen-lane-device"
                topLabel="Free"
                bottomLabel="Voice"
                straightLabel="Free"
                color="#1C1B1A"
                onClick={onCenter}
                icon={paused ? <Play size={iconSize} weight="fill" /> : speaking ? <Pause size={iconSize} weight="fill" /> : <SpeakerSimpleHigh size={iconSize} weight="fill" />}
                accessory={eyeTip({
                  open: voiceTipOpen,
                  setOpen: setVoiceTipOpen,
                  title: "Want a nicer free voice?",
                  body: tipText,
                  toggleTestid: "voice-tip-toggle",
                  popTestid: "voice-tip-popover",
                  align: "center",
                })}
              />,
              // index 1 — PRIMARY paid lane: premium / dots / cached (DEFAULT)
              <CurvedListenButton
                key="primary"
                id="left"
                testid="listen-whole-conversation-btn"
                topLabel={(blobFallback || fullyCached || showDotState) ? "Free" : `Costs (~${costLabel})`}
                bottomLabel={fullyCached ? "Cached" : blobFallback ? "Pre-paid" : showDotState ? `${dotCost} ${dotCost === 1 ? "Dot" : "Dots"}` : "Premium"}
                straightLabel={(blobFallback || fullyCached) ? "Free" : showDotState ? "Dots pay" : "You pay"}
                color={(blobFallback || fullyCached) ? "#27AE60" : showDotState ? "#3F7A4E" : "#4A5D4E"}
                onClick={fullyCached ? playWholeCached : showDotState ? onDotSpend : blobFallback ? playPrepaid : onLeft}
                warming={voice.warming && (lastModeRef.current === "self" || (showDotState && lastModeRef.current === "sponsored"))}
                icon={paused ? <Play size={iconSize} weight="fill" /> : speaking ? <Pause size={iconSize} weight="fill" /> : <SpeakerHigh size={iconSize} weight="fill" />}
                accessory={eyeTip({
                  open: leftTipOpen,
                  setOpen: setLeftTipOpen,
                  title: (blobFallback || fullyCached) ? "Free to listen" : showDotState ? "Listen with your dots" : "Premium voice",
                  body: (blobFallback || fullyCached) ? "This conversation's premium audio is already paid for — press play to listen free. The scrubber tracks position; add a new question and only that part needs dots." : showDotState ? `Spend ${dotCost} ${dotCost === 1 ? "dot" : "dots"} (1 dot covers ~5¢) to voice the NEW part of this conversation in premium voice — the already-paid parts replay free. Once voiced, replays are free.` : leftTipText,
                  toggleTestid: "left-tip-toggle",
                  popTestid: "left-tip-popover",
                  align: "left",
                })}
              />,
            ];
            // index 2 — SILO PAYS / subscribe (RIGHT) — visitors only, hidden for
            // the owner viewing their own silo.
            if (!silo.viewer_is_owner) {
              laneNodes.push(
                <CurvedListenButton
                  key="sponsored"
                  id="right"
                  testid="listen-lane-sponsored"
                  topLabel="Silo pays"
                  bottomLabel="Premium"
                  straightLabel="Subscribe"
                  color={premiumAvailable ? "#B8862F" : "#9A9892"}
                  disabled={!premiumAvailable}
                  onClick={onRight}
                  warming={voice.warming && lastModeRef.current === "sponsored"}
                  icon={paused ? <Play size={iconSize} weight="fill" /> : speaking ? <Pause size={iconSize} weight="fill" /> : <SpeakerHigh size={iconSize} weight="fill" />}
                  accessory={eyeTip({
                    open: rightTipOpen,
                    setOpen: setRightTipOpen,
                    title: "Owner-sponsored",
                    body: rightTipText,
                    toggleTestid: "right-tip-toggle",
                    popTestid: "right-tip-popover",
                    align: "right",
                  })}
                />
              );
            }
            const laneCount = laneNodes.length;
            const curLane = Math.min(Math.max(0, laneIndex), laneCount - 1);
            const goLane = (dir) => setLaneIndex((i) => Math.min(laneCount - 1, Math.max(0, Math.min(Math.max(0, i), laneCount - 1) + dir)));
            const onLaneTouchStart = (e) => { laneTouchX.current = e.touches?.[0]?.clientX ?? null; };
            const onLaneTouchEnd = (e) => {
              if (laneTouchX.current == null) return;
              const endX = e.changedTouches?.[0]?.clientX ?? laneTouchX.current;
              const dx = endX - laneTouchX.current;
              laneTouchX.current = null;
              if (Math.abs(dx) < 40) return; // deliberate horizontal swipe only
              goLane(dx > 0 ? -1 : 1); // swipe right → previous (toward Free); swipe left → next
            };
            return (
              <div
                className={`sticky z-30 transition-colors ${lanesOpen ? "border-b tpl-border shadow-sm" : ""}`}
                style={{ top: headerH, background: lanesOpen ? "var(--tpl-bg, #FDFCF8)" : "transparent" }}
                data-testid="listen-shelf-sticky"
              >
                <div className="max-w-3xl mx-auto px-6 py-2 flex flex-col items-center gap-1.5" data-testid="listen-lanes-wrap">
                <button
                  type="button"
                  data-testid="listen-lanes-toggle"
                  onClick={() => setLanesOpen((v) => !v)}
                  aria-expanded={lanesOpen}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-semibold leading-none px-2.5 py-0.5 rounded-full tpl-surface border tpl-border shadow-sm cursor-pointer hover:shadow transition-shadow"
                  style={{ color: "var(--tpl-muted, #6B6A68)" }}
                >
                  <Waveform size={11} weight="duotone" />
                  Listen to whole conversation
                </button>
                <AnimatePresence initial={false}>
                  {lanesOpen && (
                    <motion.div
                      key="listen-lanes"
                      initial={{ opacity: 0, scale: 0.3, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.3, y: -10 }}
                      transition={{ duration: 0.26, ease: "easeOut" }}
                      style={{ transformOrigin: "50% 0%", touchAction: "pan-y" }}
                      className="relative w-full flex items-center justify-center gap-2"
                      data-testid="listen-lanes"
                      onTouchStart={onLaneTouchStart}
                      onTouchEnd={onLaneTouchEnd}
                    >
                      {/* Up/down jump buttons (Randy): far-LEFT of the forest pill,
                          centered vertically; square + black. Mirrors the AI
                          Assistant's buddy-jump-arrows. */}
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5" data-testid="qa-jump-arrows">
                        <button type="button" onClick={() => { try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { window.scrollTo(0, 0); } }} data-testid="qa-jump-top" aria-label="Jump to top"
                          className="w-9 h-9 rounded-lg bg-[#1C1B1A] text-white shadow-sm flex items-center justify-center hover:bg-[#3A4A3E] transition-colors">
                          <CaretUp size={16} weight="bold" />
                        </button>
                        <button type="button" onClick={() => { try { window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); } catch { window.scrollTo(0, document.body.scrollHeight); } }} data-testid="qa-jump-bottom" aria-label="Jump to bottom"
                          className="w-9 h-9 rounded-lg bg-[#1C1B1A] text-white shadow-sm flex items-center justify-center hover:bg-[#3A4A3E] transition-colors">
                          <CaretDown size={16} weight="bold" />
                        </button>
                      </div>
                      {/* Feb 16 2026 (Randy) — MODEL picker pill, absolute-
                          positioned as a mirror of the ^v jump-arrows on the
                          left. Placed here so the composer sits lower/cleaner
                          and model switching lives with the other listen-shelf
                          controls. Session-only pick, refresh reverts to the
                          silo default. */}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col items-center" data-testid="qa-model-pill-wrap">
                        <button
                          type="button"
                          onClick={() => setLlmPickerOpen(true)}
                          data-testid="public-qa-llm-picker-btn"
                          title={llmOverride?.model
                            ? `Session model: ${llmOverride.model}. Tap to change or revert to default.`
                            : "Pick a different LLM for this tab-session. Refresh reverts to the silo's default."}
                          className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full border tpl-border tpl-surface tpl-text hover:tpl-accent transition-colors flex items-center gap-1 shadow-sm"
                        >
                          <SlidersHorizontal size={10} weight="bold" />
                          Model{llmOverride?.model ? `: ${labelForModel(llmOverride.model)}` : ""}
                        </button>
                      </div>
                      <button
                        type="button"
                        data-testid="listen-lane-prev"
                        aria-label="Previous listen option"
                        onClick={() => goLane(-1)}
                        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-transform hover:scale-110 active:scale-95"
                        style={{ color: "var(--tpl-text, #1C1B1A)", visibility: curLane > 0 ? "visible" : "hidden" }}
                      >
                        <CaretLeft size={20} weight="bold" />
                      </button>
                      <div className="flex items-center justify-center" style={{ minWidth: 96 }}>
                        {laneNodes[curLane]}
                      </div>
                      <button
                        type="button"
                        data-testid="listen-lane-next"
                        aria-label="Next listen option"
                        onClick={() => goLane(1)}
                        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-transform hover:scale-110 active:scale-95"
                        style={{ color: "var(--tpl-text, #1C1B1A)", visibility: curLane < laneCount - 1 ? "visible" : "hidden" }}
                      >
                        <CaretRight size={20} weight="bold" />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                {lanesOpen && visibleStops.length >= 1 && (
                  <>
                    {scrubScope && (
                      <div className="w-full flex items-center justify-center gap-2 mt-2" data-testid="qa-scrubber-scope-chip">
                        <span className="text-xs font-medium text-[#4A4A48]">
                          This exchange · {scrubScope.segIdx + 1} of {qaSegments.length}
                        </span>
                        <button
                          type="button"
                          data-testid="qa-scrubber-scope-reset"
                          onClick={() => setScrubScope(null)}
                          className="text-xs font-semibold text-[#4A5D4E] underline underline-offset-2 hover:text-[#3A4A3E] transition-colors"
                        >
                          Whole conversation
                        </button>
                      </div>
                    )}
                    <ConversationScrubber
                      stops={visibleStops}
                      currentStop={scrubCurrentStop}
                      frac={playPos.frac || 0}
                      playing={scrubOwnsAudio && voice.state === "speaking"}
                      onPlayPause={() => {
                        if (scrubOwnsAudio && (speaking || paused)) { voice.pauseOrResume(); return; }
                        playStop(visibleStops, scrubCurrentStop >= 0 ? scrubCurrentStop : 0, lastModeRef.current, scrubScope);
                      }}
                      onPrev={() => playStop(visibleStops, Math.max(0, (scrubCurrentStop < 0 ? 0 : scrubCurrentStop) - 1), lastModeRef.current, scrubScope)}
                      onNext={() => playStop(visibleStops, Math.min(visibleStops.length - 1, (scrubCurrentStop < 0 ? 0 : scrubCurrentStop) + 1), lastModeRef.current, scrubScope)}
                      onJump={(i) => playStop(visibleStops, i, lastModeRef.current, scrubScope)}
                      color="#4A5D4E"
                      testidPrefix="qa-scrubber"
                    />
                  </>
                )}
              </div>
              </div>
            );
          })()}
        <div className="max-w-3xl mx-auto px-6 py-10 flex flex-col gap-6" data-testid="silo-chat-feed">
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div key={i} ref={(el) => (messageRefs.current[i] = el)} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "user" ? (
                  <div data-testid={`message-user-${i}`} className="tpl-bubble-user rounded-2xl rounded-tr-sm p-5 max-w-[85%] leading-relaxed whitespace-pre-wrap break-words">{m.text}</div>
                ) : (
                  <div data-testid={`message-ai-${i}`}
                    className={`siloart-encased relative tpl-surface border rounded-2xl rounded-tl-sm p-6 shadow-sm max-w-[95%] ${m.outOfScope ? "" : "tpl-border"}`}
                    style={{
                      // Feb 16 2026 (Randy) — HARDEN the AI-answer surface so it
                      // never flickers/disappears on iOS Safari. Two-part defense:
                      // (1) explicit inline background as a fallback in case any
                      //     CSS rule races/fails to apply `.tpl-surface`, and
                      // (2) GPU-layer promotion (transform + will-change +
                      //     backfaceVisibility) so iOS keeps the paint layer
                      //     stable across scroll + composer animations. Root
                      //     cause was iOS compositor dropping the bubble's paint
                      //     layer during scroll + motion.div opacity animation,
                      //     revealing the starry canvas behind it briefly.
                      backgroundColor: "var(--tpl-surface, #FFFFFF)",
                      transform: "translateZ(0)",
                      willChange: "transform",
                      backfaceVisibility: "hidden",
                      ...(m.outOfScope ? { borderColor: "var(--tpl-secondary)" } : {}),
                    }}>
                    {m.outOfScope && (
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "var(--tpl-secondary)" }}>
                        <Warning size={14} weight="duotone" /> Outside this silo
                      </div>
                    )}
                    <div className="prose-theo whitespace-pre-wrap break-words">
                      <ReactMarkdown
                        components={{
                          // Force every URL — Markdown link or auto-detected — to open in a
                          // new tab on both desktop and mobile. `rel` blocks tabnabbing.
                          // Feb 21, 2026 — also style them as obviously
                          // clickable (underline + accent color) so URLs
                          // from silo content stand out as visit-worthy.
                          a: ({ node: _node, href, children, ...rest }) => (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#4A5D4E] underline decoration-[#4A5D4E]/40 underline-offset-2 hover:decoration-[#4A5D4E] hover:text-[#3A4A3E] transition-colors break-words"
                              {...rest}
                            >
                              {children}
                            </a>
                          ),
                        }}
                        urlTransform={(u) => u}
                      >
                        {linkifyUrls(m.text)}
                      </ReactMarkdown>
                    </div>
                    {/* Jun 2026 (Randy) — bottom-right speaker now reads the
                        QUESTION + this ANSWER (two voices, like the Assistant),
                        and turns GREEN when that Q+A pair is already cached (free
                        24h replay). Mild shimmer while it spins up. */}
                    {silo.voice_enabled && !m.error && m.text && (() => {
                      const qPrev = messages[i - 1]?.role === "user" ? (messages[i - 1].text || "").trim() : "";
                      const segCached = coverage.cached.includes(i) && (!qPrev || coverage.cached.includes(i - 1));
                      const segKey = `seg:${i}`;
                      const segActive = voice.playKey === segKey;
                      // The drawer narrows to THIS exchange while it plays, so
                      // the listener can skip a long question. (Randy, Sep 2026)
                      const segScope = { segIdx: qaSegments.findIndex((s) => s.aMsgIdx === i), key: segKey };
                      const playThis = async () => {
                        // A GREEN/cached pair replays FREE for ANY visitor (incl.
                        // an anon on a shared prepaid convo) — no key, no charge.
                        if (segCached) {
                          lastModeRef.current = "sponsored";
                          setScrubScope(segScope);
                          voice.speakCached(
                            [
                              ...(qPrev ? [{ role: "user", text: qPrev, idx: i - 1, voice: questionVoice }] : []),
                              { role: "assistant", text: m.text, idx: i, voice: answerVoice },
                            ],
                            { key: segKey },
                          );
                          return;
                        }
                        // Jun 2026 (Randy) — an UNCACHED pair is voiced via the DOT
                        // system (sponsored), NOT the listener's own key. Cost scales
                        // with length: 1 dot per ~5¢, rounded up (same rule as the
                        // whole-conversation pill). Applies to EVERY dots-mode visitor
                        // — fan, creator, affiliate, OR anon (abuse-capped server-side).
                        const segTurns = [];
                        if (qPrev) segTurns.push({ role: "user", text: qPrev, idx: i - 1, voice: questionVoice });
                        segTurns.push({ role: "assistant", text: m.text, idx: i, voice: answerVoice });
                        const playSponsored = () => {
                          lastModeRef.current = "sponsored";
                          setScrubScope(segScope);
                          voice.speakConversation(segTurns, { answerVoice, questionVoice, mode: "sponsored", key: segKey });
                        };
                        const premiumAvail = !!(silo.voice_enabled && silo.voice_ready);
                        // Owner hasn't funded premium voice → tell the visitor (don't
                        // burn dots on a silent fallback).
                        if (!premiumAvail) { setCreatorVoiceOffOpen(true); return; }
                        const mode = interactions.state?.mode;
                        // Owner / subscriber (dollar) → premium on the owner's dime, no dots.
                        if (mode !== "dots") { playSponsored(); return; }
                        // DOTS-mode visitor — spend dots scaled to this pair's length.
                        const pairChars = `${qPrev ? qPrev + " " : ""}${m.text}`.replace(/\s+/g, " ").trim().length;
                        const pairDotCost = Math.max(1, Math.ceil((pairChars * 0.0015) / 5));
                        const dotsAvailable = interactions.state?.available || 0;
                        if (dotsAvailable < pairDotCost) {
                          if (viewer.isOperator) {   // creator/affiliate out of dots → self-pay this piece
                            lastModeRef.current = "self";
                            setScrubScope(segScope);
                            voice.speakConversation(segTurns, { answerVoice, questionVoice, mode: "self", key: segKey });
                            return;
                          }
                          setOutOfDotsCtaOpen(true); return;   // fan / anon out of dots → 3-ways CTA
                        }
                        try {
                          const r = await api.post(`/s/${slug}/voice/dot-spend`, { dots: pairDotCost });
                          if (r.data?.interactions) interactions.applyFromAsk(r.data.interactions);
                        } catch (e) {
                          const det = e?.response?.data?.detail;
                          if (det && typeof det === "object" && det.interactions) interactions.applyFromAsk(det.interactions);
                          if (det?.code === "NOT_ENOUGH_DOTS") { setOutOfDotsCtaOpen(true); return; }
                          toast.error("Couldn't start dot playback — please try again.");
                          return;
                        }
                        playSponsored();
                      };
                      return (
                        <button
                          type="button"
                          data-testid={`message-replay-${i}`}
                          title={segCached ? "Cached — replay this question & answer free" : "Listen to this question & answer"}
                          aria-label="Listen to this question and answer"
                          onClick={() => { if (segActive && (voice.state === "speaking" || voice.state === "paused")) { voice.pauseOrResume(); return; } playThis(); }}
                          className={`absolute bottom-1.5 right-2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-black/5 active:bg-black/10 transition-colors ${voice.warming && segActive ? "voice-warming" : ""}`}
                          style={{ color: segCached ? "#27AE60" : "var(--tpl-muted, #6B6A68)" }}
                        >
                          <SpeakerHigh size={14} weight={(segActive && voice.state === "speaking") || segCached ? "fill" : "regular"} />
                        </button>
                      );
                    })()}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {loading && (
            <div className="flex justify-start">
              <div className="tpl-surface border tpl-border rounded-2xl rounded-tl-sm p-5 shadow-sm flex gap-1.5">
                <span className="w-2 h-2 rounded-full tpl-accent-bg animate-bounce" />
                <span className="w-2 h-2 rounded-full tpl-accent-bg animate-bounce [animation-delay:0.15s]" />
                <span className="w-2 h-2 rounded-full tpl-accent-bg animate-bounce [animation-delay:0.3s]" />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Composer area — Feb 19, 2026 iter-2: wrapped in ComposerAreaSplitter
          so the horizontal line between chat history and composer is now
          a drag handle (desktop + mobile, session-only). squeezeLevel
          drives the cascade that hides footer / pill / tip as space tightens. */}
      <ComposerAreaSplitter>
        {({ squeezeLevel }) => (
        <div className="max-w-3xl mx-auto px-6 py-0.5">
          {silo.resting ? null : (<>
          {/* Anonymous-quota wall REMOVED Jun 2026 (clean-slate teardown). The
              composer renders directly now — no visitor gating. */}
          <div className="relative">
            {/* Jun 2026 (Randy) — "keep this conversation" sign-up nudge. Appears
                ONCE, after the anon's 2nd OWN question (anonAskCount — ignores any
                preloaded shared-bookmark turns). Dismissible (won't return this
                session). Opens the in-place sign-up card (no page jump) so the
                conversation stays on screen; after sign-up we claim the session so
                it's recallable on any device. Hidden for logged-in users. */}
            {(() => {
              const u = (typeof getUser === "function" ? getUser() : null);
              const dismissed = (typeof sessionStorage !== "undefined") && sessionStorage.getItem(`keep_convo_nudge_dismissed_${slug}`);
              if (u || dismissed || anonAskCount < 2) return null;
              return (
                <div className="mb-3 px-4 py-2.5 rounded-xl border tpl-border tpl-surface tpl-text text-sm flex items-center gap-3" data-testid="anon-save-nudge">
                  <Sparkle size={14} weight="duotone" className="text-[#4A5D4E] shrink-0" />
                  <span className="flex-1">
                    Enjoying this?{" "}
                    <button
                      onClick={() => { pendingKeepConvoRef.current = true; setFavCtaStar(false); setFavCtaOpen(true); }}
                      className="text-[#4A5D4E] underline font-medium"
                      data-testid="anon-save-nudge-cta"
                    >
                      Sign up free
                    </button>{" "}
                    to keep this conversation — recall it anywhere.
                  </span>
                  <button
                    onClick={() => {
                      try { sessionStorage.setItem(`keep_convo_nudge_dismissed_${slug}`, "1"); } catch {/* */}
                      setNudgeTick((t) => t + 1);
                    }}
                    data-testid="anon-save-nudge-dismiss"
                    className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] hover:text-[#1C1B1A]"
                  >
                    Dismiss
                  </button>
                </div>
              );
            })()}
            {/* Feb 19, 2026 — owner-saveable composer height. The previous
                inline textarea/send block is now wrapped in ComposerResizable.
                Default size is the smallest single-line (44px). Anyone can
                drag the custom handle; only the owner's last drag persists. */}
            <ComposerResizable
              siloId={silo.id}
              slug={slug}
              siloOwnerHeightPx={silo.composer_height_px}
              viewerIsOwner={!!silo.viewer_is_owner}
              input={input}
              setInput={setInput}
              onKeyDown={onKeyDown}
              onSend={() => ask()}
              onSendBlocked={() => setTextTopupOpen(true)}
              loading={loading}
              placeholder={placeholder}
              voiceEnabled={!!silo.voice_enabled}
              voiceReady={!!silo.voice_ready}
              voiceState={voice.state}
              onVoicePress={voice.toggle}
              onStopSpeaking={voice.stopSpeaking}
            />
            {/* Always-on subscribe / sign-up pill — Feb 17, 2026.
                Jul 25, 2026 (Randy): hidden entirely when "Turn off tabs" is
                active (both live in the hamburger dropdown instead), and both
                pills shrunk ~50% when shown so the footer stays thin. */}
            {!tabsOff && squeezeLevel < 2 && (showSubscribe || showTip) && (
                <div className="flex items-center justify-center gap-2 mt-1" data-testid="subscribe-pill-container">
                  {showSubscribe && (
                    <button onClick={onSubscribePillClick} data-testid="subscribe-pill"
                      className="text-[10px] px-2 py-0.5 rounded-full border tpl-border tpl-surface tpl-text hover:tpl-accent transition-colors">
                      Subscribe to {silo.name || "this silo"} · {subPriceLabel}
                    </button>
                  )}
                  {showTip && (
                    <button onClick={() => setTipOpen(true)} data-testid="public-tip-btn"
                      className={
                        tipStyle === "pill" ? "text-[10px] px-2 py-0.5 rounded-full hover:opacity-90 flex items-center gap-1 transition-opacity" :
                        tipStyle === "pill-outlined" ? "text-[10px] px-2 py-0.5 rounded-full border-2 hover:opacity-80 flex items-center gap-1 transition-opacity" :
                        tipStyle === "flat-bar" ? "text-[10px] px-2 py-1 uppercase tracking-[0.18em] hover:opacity-90 flex items-center gap-1 transition-opacity" :
                        tipStyle === "floating-card" ? "text-[10px] px-2 py-1 rounded-lg shadow-lg hover:shadow-xl flex items-center gap-1 transition-shadow" :
                        tipStyle === "inline-link" ? "text-[10px] underline hover:no-underline flex items-center gap-1 bg-transparent" :
                        "text-[10px] px-1.5 py-0.5 rounded-md font-medium hover:opacity-90 flex items-center gap-1 transition-opacity"
                      }
                      style={
                        tipStyle === "pill-outlined" ? { borderColor: tipBg, color: tipBg, background: "transparent" } :
                        tipStyle === "inline-link" ? { color: tipBg } :
                        { background: tipBg, color: tipFg }
                      }>
                      💛 Tip {silo.name}
                    </button>
                  )}
                </div>
            )}
            {/* Feb 16 2026 (Randy) — Model picker pill relocated to the
                top-right of the "LISTEN TO WHOLE CONVERSATION" shelf
                (mirror of the ^v jump-arrows on the left). Removes clutter
                below the composer so the text area sits lower/cleaner. */}
          </div>
          </>)}
          {/* Jul 25, 2026 (Randy) — the "Creator may review conversations…"
              fine-print line was removed for a cleaner, shorter footer;
              "Report this silo" moved into the hamburger dropdown. */}
        </div>
        )}
      </ComposerAreaSplitter>

      <ReportContactModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        context="silo_report"
        siloId={silo.id}
        siloLabel={silo.name}
      />

      {/* Iter160e (Randy) — session-only LLM surface picker. Same modal
          the YTRE tab uses; both surfaces write to sessionStorage under
          `public_ask` so a pick made on one surface applies on the
          other for the same tab-session. */}
      {llmPickerOpen && (
        <SessionLlmPickerModal
          surface="public_ask"
          siloId={silo.id}
          siloSlug={slug}
          onClose={() => setLlmPickerOpen(false)}
          onSaved={() => setLlmOverrideTick((n) => n + 1)}
        />
      )}

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        messages={messages}
        siloName={silo.name}
        siloSlug={silo.slug}
        summaryEndpoint={`/s/${slug}/export/summary`}
        bookmarkId={loadedBookmarkId}
      />

      {/* Jul 2026 (Randy) — educational Clear confirmation. "Clear" only resets
          what's on screen; the full conversation is retained (Recall 30d, the
          creator's Chats 30d) and answers stay pushable to the Workbench from
          the Usage report (last 50, 30d). Concise, with the real retention times. */}
      {clearConfirmOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="clear-confirm-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setClearConfirmOpen(false)} />
          <div className="modal-safe relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <div className="flex items-center gap-2 text-[#1C1B1A]">
                <Trash size={18} weight="duotone" className="text-[#8A6D3B]" />
                <h3 className="font-serif-display text-lg leading-none">Clear this conversation?</h3>
              </div>
              <p className="text-[13px] text-[#57534E] mt-2.5">This just resets what's on screen — <strong>nothing is deleted.</strong></p>
              <ul className="mt-3 space-y-1.5 text-[12.5px] text-[#57534E]">
                <li className="flex gap-2"><span className="text-[#2F6B3A]">✓</span> Saved in <strong>Recall</strong> for <strong>30 days</strong> — reopen it anytime.</li>
                <li className="flex gap-2"><span className="text-[#2F6B3A]">✓</span> Kept in the creator's <strong>Chats</strong> for <strong>30 days</strong>.</li>
                <li className="flex gap-2"><span className="text-[#2F6B3A]">✓</span> Answers stay pushable to the <strong>Workbench</strong> from the Usage report — your last 50, for 30 days.</li>
              </ul>
            </div>
            <div className="flex items-center gap-2 px-5 py-4 bg-[#FAFAF7] border-t border-[#EDEBE6]">
              <button
                onClick={() => setClearConfirmOpen(false)}
                data-testid="clear-confirm-cancel"
                className="flex-1 text-[13px] font-medium text-[#57534E] hover:text-[#1C1B1A] py-2 rounded-lg border border-[#E5E3DE] hover:bg-white transition-colors"
              >
                Keep it
              </button>
              <button
                onClick={() => { setClearConfirmOpen(false); clearConversation(); }}
                data-testid="clear-confirm-proceed"
                className="flex-1 text-[13px] font-medium text-white py-2 rounded-lg bg-[#8A2E2E] hover:bg-[#7A2626] transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}


      {/* OPEN-TIME "complete the audio?" popup (Jun 2026, Randy) — fires when the
          owner loads their own bookmark whose audio is only PARTIAL. */}
      {completeAudioPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" data-testid="public-complete-audio-popup">
          <style>{`@keyframes gapslide { 0% { left: -35% } 100% { left: 100% } }`}</style>
          <div className="modal-safe bg-white rounded-xl shadow-xl border border-[#E5E5E0] max-w-sm w-full p-5 relative text-[#1C1B1A]">
            <button type="button" onClick={() => { setCompletingAudio(false); setCompleteAudioPopup(null); }} data-testid="public-complete-audio-close"
              className="absolute top-3 right-3 text-[#9b9a96] hover:text-[#1C1B1A] p-1" title="Close"><X size={16} /></button>
            <div className="text-[15px] font-semibold pr-6">Complete this conversation's audio?</div>
            <p className="mt-1.5 text-sm text-[#6B6A68]">
              {completeAudioPopup.missCount} {completeAudioPopup.missCount === 1 ? "part hasn't" : "parts haven't"} been voiced yet. Finish {completeAudioPopup.missCount === 1 ? "it" : "them"} and the whole conversation will play <span className="font-semibold text-[#27AE60]">free</span> for anyone you share it with.
            </p>
            {completingAudio ? (
              <div className="mt-4" data-testid="public-complete-audio-progress">
                <div className="h-2 w-full rounded-full bg-[#EDEDE7] overflow-hidden relative">
                  <div className="h-full w-[35%] bg-[#27AE60] rounded-full absolute top-0" style={{ animation: "gapslide 1.1s ease-in-out infinite" }} />
                </div>
                <div className="mt-2 text-xs text-[#6B6A68] text-center">Recording the missing parts…</div>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setCompleteAudioPopup(null)} data-testid="public-complete-audio-skip"
                  className="px-3 py-1.5 rounded-md border border-[#E5E5E0] text-sm text-[#6B6A68] hover:text-[#1C1B1A]">Not now</button>
                <button type="button" onClick={completeBookmarkAudio} data-testid="public-complete-audio-fill"
                  className="px-3 py-1.5 rounded-md bg-[#27AE60] text-white text-sm font-semibold hover:bg-[#229954] flex items-center gap-1.5">
                  Complete it
                  <span className="text-[11px] bg-white/20 rounded px-1.5 py-0.5">~{completeAudioPopup.cents < 1 ? completeAudioPopup.cents.toFixed(1) : Math.round(completeAudioPopup.cents)}¢</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Left-edge Side-Bumps: lower utility drawer + upper bookmark shelf.
          Builds the conversion funnel for Anonymous → Subscriber → Creator. */}
      <SideBumps
        silo={silo}
        messages={messages}
        setMessages={setMessages}
        onLoadBookmark={applyLoadedBookmark}
        mode="left"
        voiceUserSelectable={!!silo.voice_enabled && !!silo.voice_user_selectable}
        voiceOptions={VOICE_OPTIONS}
        selectedVoice={selectedVoice || silo.voice_id || "onyx"}
        onSelectVoice={onSelectVoice}
        interactions={interactions.state}
        onInteractionTip={() => setTipOpen(true)}
        onInteractionSubscribe={() => {
          if (getToken()) setSubscribePopupOpen(true); else { setFavCtaStar(false); setFavCtaOpen(true); }
        }}
        onInteractionFavorite={() => toggleGhostStar(!ghostStarred)}
        onInteractionAddHomeScreen={() => setHomeHowToOpen(true)}
        onBookmarkSaved={() => interactions.award("bookmark")}
      />

      {/* Feb 14, 2026 — Length shelf (answer-style picker). Visitor-facing,
          left side, top slot by default. Position/style configurable in Bump
          Studio like every other bump. */}
      <LengthBump
        silo={silo}
        sessionId={sessionId || null}
        activePreset={answerStyle.preset}
        activeCustom={answerStyle.custom}
        onChange={({ preset, custom }) => setAnswerStyle({ preset, custom })}
        viewerUser={viewer?.user}
      />

      {/* Right-edge Discover + Invite drawers (iter-31 Phase 1). */}
      <DiscoverBumps
        silo={silo}
        conversationSessionId={sessionId}
        loadedBookmarkId={loadedBookmarkId}
        hasConversation={messages.length > 0}
        onShareConversation={() => setShareConvOpen(true)}
        onRequireSignup={() => { setFavCtaStar(false); setFavCtaOpen(true); }}
      />
      {shareConvOpen && (
        <ShareConversationSheet
          slug={silo.slug}
          siloName={silo.name}
          messages={messages}
          bookmarkId={loadedBookmarkId || null}
          answerVoice={selectedVoice || silo.voice_id || "onyx"}
          onSaved={(id) => setLoadedBookmarkId(id)}
          onClose={() => setShareConvOpen(false)}
        />
      )}

      {/* iter-35g Phase 2 — dynamic bump drawers (Workbench/Refinery slots).
          Owner-only; the component hides itself for non-owners. */}
      <DynamicBumps silo={silo} />

      {/* iter-26: Tip flow — supports both Connect-ready and pre-Connect creators */}
      <TipModal open={tipOpen} slug={slug} creatorName={silo.name} onClose={() => setTipOpen(false)} />

      {/* Visitor heavy-use meter wall REMOVED Jun 2026 (clean-slate teardown). */}
      <SubscribePopup
        open={subscribePopupOpen}
        slug={slug}
        silo={silo}
        onClose={() => setSubscribePopupOpen(false)}
        onSubscribed={async () => {
          try {
            const s = await api.get(`/s/${slug}`);
            setSilo(s.data);
          } catch { /* non-fatal */ }
        }}
      />
      {postStripeA2HS && <A2HSPopup context="post_subscribe" triggerNow={true} siloName={silo?.name} />}
      <OwnerByokNudgeModal
        open={ownerByokNudge.open}
        onClose={() => setOwnerByokNudge({ open: false, ownerName: "" })}
        siloSlug={slug}
        siloOwnerName={ownerByokNudge.ownerName || silo.name}
      />
      {/* Jun 2026 — Voice OpenAI top-up CTA. Only the owner ever opens this
          (visitors get a gentle bubble instead). Pre-focuses this silo and the
          OpenAI bucket so funding voice is one tap. */}
      <VoucherTopupModal
        open={voiceTopupOpen}
        onClose={() => setVoiceTopupOpen(false)}
        prefocusedSiloId={silo.id}
        defaultProvider="openai"
        onSuccess={() => setVoiceTopupOpen(false)}
      />

      {/* Jul 2026 — OWNER text-answer funding. When the owner previews their
          own public Q&A and the silo has no AI credit (no key + empty voucher),
          the locked Send button opens this so they can fund answers in one tap.
          Visitors never see it (the guard is owner-only). */}
      <VoucherTopupModal
        open={textTopupOpen}
        onClose={() => setTextTopupOpen(false)}
        prefocusedSiloId={silo.id}
        defaultProvider="gemini"
        onSuccess={() => setTextTopupOpen(false)}
      />

      {/* Jun 2026 — Visitor billing ("dots") call-to-actions. */}
      <Q4NudgePopup
        open={q4Open}
        siloName={silo.name}
        onClose={() => { setQ4Open(false); window.dispatchEvent(new Event("mysilo:open-tools-drawer")); }}
        onOpenDrawer={() => { setQ4Open(false); window.dispatchEvent(new Event("mysilo:open-tools-drawer")); }}
      />
      <FavoriteSignupCTA
        open={favCtaOpen}
        slug={slug}
        siloName={silo.name}
        signedIn={!!viewer.user}
        starOnSignup={favCtaStar}
        onClose={() => { pendingSubscribeRef.current = false; pendingKeepConvoRef.current = false; setFavCtaOpen(false); window.dispatchEvent(new Event("mysilo:open-tools-drawer")); }}
        onSignedUp={async () => {
          setFavCtaOpen(false);
          await interactions.reload();
          try { const s = await api.get(`/s/${slug}`); setSilo(s.data); } catch { /* non-fatal */ }
          // Jun 2026 (Randy) — came from the "keep this conversation" nudge: claim
          // the anon-created session for the new account so it's recallable on any
          // device. Keep the conversation on screen (we don't reset messages) and
          // DON'T open the tools drawer.
          if (pendingKeepConvoRef.current) {
            pendingKeepConvoRef.current = false;
            if (sessionId) {
              try { await api.post(`/s/${slug}/claim-session`, { session_id: sessionId }); } catch { /* non-fatal */ }
            }
            toast.success("Saved — recall this conversation anytime from the Recall menu.");
            return;
          }
          // If they came here by clicking "Subscribe", continue into the
          // Subscribe popup now that they're a member.
          if (pendingSubscribeRef.current) {
            pendingSubscribeRef.current = false;
            setSubscribePopupOpen(true);
          } else {
            window.dispatchEvent(new Event("mysilo:open-tools-drawer"));
          }
        }}
        onAlreadyMemberFilled={() => { setFavCtaOpen(false); interactions.reload(); window.dispatchEvent(new Event("mysilo:open-tools-drawer")); }}
      />
      <CeilingExceededWall
        open={ceilingWallOpen}
        siloName={silo.name}
        isGift={!!interactions.state?.dollar?.is_gift}
        hasTokens={(interactions.state?.available || 0) > 0}
        onClose={() => setCeilingWallOpen(false)}
        onTip={() => { setCeilingWallOpen(false); setTipOpen(true); }}
        onUseTokens={() => {
          setCeilingWallOpen(false);
          const q = pendingTokenQRef.current; pendingTokenQRef.current = null;
          if (q) ask(q, { useTokens: true });
        }}
      />
      {homeHowToOpen && <HomeScreenHowTo onClose={() => setHomeHowToOpen(false)} />}
      <VoiceLaneCTA open={becomeCreatorCtaOpen} variant="become-creator" onClose={() => setBecomeCreatorCtaOpen(false)} />
      <VoiceLaneCTA
        open={outOfDotsCtaOpen}
        variant="out-of-dots"
        signedIn={!!viewer.user}
        onClose={() => setOutOfDotsCtaOpen(false)}
        onTip={() => { setOutOfDotsCtaOpen(false); setTipOpen(true); }}
        onSubscribe={() => { if (getToken()) setSubscribePopupOpen(true); else { setFavCtaStar(false); setFavCtaOpen(true); } }}
      />
      <VoiceLaneCTA open={voiceSetupCtaOpen} variant="setup-funding" onClose={() => setVoiceSetupCtaOpen(false)} />
      <VoiceLaneCTA
        open={creatorVoiceOffOpen}
        variant="creator-voice-off"
        siloName={silo.name}
        onClose={() => setCreatorVoiceOffOpen(false)}
        onFundVoice={() => { setCreatorVoiceOffOpen(false); setFundVoiceOpen(true); }}
      />
      <FundVoiceModal
        open={fundVoiceOpen}
        slug={slug}
        creatorName={silo.name}
        onClose={() => setFundVoiceOpen(false)}
      />

      {/* Jun 2026 (Randy) — choose where the whole-conversation read starts. */}
      {playChoiceOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
          data-testid="play-choice-overlay"
          onClick={() => setPlayChoiceOpen(false)}
        >
          <div
            className="modal-safe bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
            data-testid="play-choice-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[#1C1B1A] mb-1">Where should it start?</h3>
            <p className="text-sm text-[#6B6A68] mb-5">You already have a few exchanges here — pick up from the latest, or replay the whole thread.</p>
            <div className="space-y-2">
              <button
                type="button"
                data-testid="play-choice-beginning"
                onClick={() => { setPlayChoiceOpen(false); playStop(scrubStops, 0, pendingPlayModeRef.current, null); }}
                className="w-full py-3 rounded-full bg-[#4A5D4E] text-white text-sm font-semibold hover:bg-[#3A4A3E] transition-colors"
              >
                Play from the beginning
              </button>
              <button
                type="button"
                data-testid="play-choice-latest"
                onClick={() => { setPlayChoiceOpen(false); playStop(scrubStops, Math.max(0, scrubStops.findIndex((s) => s.segIdx === qaSegments.length - 1)), pendingPlayModeRef.current, null); }}
                className="w-full py-3 rounded-full border border-[#4A5D4E] text-[#4A5D4E] text-sm font-semibold hover:bg-[#F0F1EE] transition-colors"
              >
                Start from the latest question
              </button>
              <button
                type="button"
                data-testid="play-choice-cancel"
                onClick={() => setPlayChoiceOpen(false)}
                className="w-full py-2 text-xs text-[#9A9892] hover:text-[#6B6A68] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
